/**
 * Unified Onboarding Reviewer Panel
 *
 * Side-pane reviewer for /contractor-onboarding. UI shape comes from
 * origin/main, but the wiring intentionally preserves beta-v2's current
 * /admin/compliance-review behaviors.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import { usePermissions } from "@/data/PermissionsContext";
import { useOnboarding } from "@/data/contractor/onboarding/OnboardingProvider";
import { useToast } from "@/hooks/use-toast";
import { ContractorService } from "@/services/contractor.service";
import {
  isCanonicalInternalComplianceReviewEnabled,
} from "@/services/individual-compliance-ops-review.service";
import type { Contractor } from "@/data/types";
import {
  buildCanonicalOnboardingReviewContext,
  resolveCanonicalOnboardingParent,
  type CanonicalOnboardingRelatedPerson,
} from "@/data/contractor/onboarding/canonicalComplianceReviewContext";
import {
  CanonicalIndividualComplianceReviewPanel,
} from "@/components/compliance-review/CanonicalIndividualComplianceReviewPanel";
import {
  CanonicalCompanyComplianceReadPanel,
} from "@/components/compliance-review/CanonicalCompanyComplianceReadPanel";
import {
  CanonicalRelationshipReviewReadPanel,
} from "@/components/compliance-review/CanonicalRelationshipReviewReadPanel";
import {
  CanonicalCompanyCompliancePolicyEditor,
} from "@/components/compliance-review/CanonicalCompanyCompliancePolicyEditor";
import {
  CanonicalIndividualCompliancePolicyEditor,
} from "@/components/compliance-review/CanonicalIndividualCompliancePolicyEditor";
import {
  defaultPayProfile,
  type ContractorPayProfile,
  type SpecArchetype,
} from "@/data/contractorPayProfile";
import {
  getOnboardingComplianceRequirementSettings,
  getRequiredOnboardingCompliance,
  type AgreementVariant,
  type ComplianceRequirements,
} from "@/data/contractor/compliance/complianceRequirements";
import type {
  ComplianceDocument,
  ComplianceDocumentType,
} from "@/data/contractor/compliance/complianceDocuments";
import { getComplianceStatus } from "@/data/contractor/compliance/complianceStatus";
import { onboardingContractorId } from "@/data/contractor/onboarding/documents";
import {
  getCanonicalSessionStatusLabel,
} from "@/data/contractor/onboarding/lifecycle";
import {
  getOnboardingStepLabel,
} from "@/data/contractor/onboarding/stepLabels";
import { getOnboardingSteps } from "@/data/contractor/onboarding/stepRegistry";
import type {
  ContractorOnboardingDraft,
  OnboardingComplianceRequirementRuleSet,
  OnboardingInviteToken,
  OnboardingSession,
} from "@/data/contractor/onboarding/types";
import {
  activateInstallerComplianceCrew,
  approveInstallerComplianceDocument,
  getInstallerComplianceDocumentReadUrl,
  listInstallerComplianceCrewRoster,
  rejectInstallerComplianceDocument,
  type InstallerComplianceCrewRosterRow,
} from "@/data/contractor/onboarding/backend/installerComplianceReview";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardList,
  Clock,
  ExternalLink,
  FileCheck,
  MapPin,
  ScrollText,
  Send,
  ShieldCheck,
  User,
  X,
  XCircle,
} from "lucide-react";

const MIN_REASON = 10;
const APPROVE_CONFIRM = "CONFIRM";
const DEFAULT_INSTALLER_PORTAL_URL = "http://localhost:3000";
const AGREEMENT_VERSION = "v1.9-R2";

const AGREEMENT_LABELS: Record<AgreementVariant, string> = {
  "1099_msa": "1099 Master Service Agreement",
  w2_offer_letter: "W-2 Offer Letter",
  branch_sublicense: "Branch Sublicense Agreement",
  crew_acknowledgment: "Crew Acknowledgment",
  helper_acknowledgment: "Helper Acknowledgment",
  certification_agreement: "Manufacturer Certification Agreement",
};

const MSA_INTRO =
  "This Master Service Agreement (Agreement) is entered into between Koredor, LLC (Koredor) and the independent contractor identified during onboarding (Installer). This Agreement establishes the terms for Installer's participation in the Koredor platform and is designed to provide maximum legal protections for Koredor and participating Contractors.";

const MSA_SECTIONS: Array<{ title: string; paragraphs: string[] }> = [
  {
    title: "1. Definitions",
    paragraphs: [
      "Contractor - The licensed home improvement company engaging services through the Koredor platform.",
      "Customer - The homeowner, property owner, or occupant receiving services.",
      "Job - An accepted work assignment with a defined scope, location, schedule, and compensation.",
      "Opportunity - A work opportunity presented through the Koredor platform.",
      "Installer-Related Issue - Any claim, defect, deficiency, callback, warranty issue, injury, death, property damage, code violation, permit issue, lien claim, labor dispute, tax dispute, regulatory action, delay, abandonment, or other matter to the extent arising from Installer's operations, personnel, vehicles, equipment, or subcontractors.",
      "Portal - The Koredor software platform and related applications.",
    ],
  },
  {
    title: "2. Independent Contractor Relationship",
    paragraphs: [
      "Installer is an independent contractor and not an employee, partner, joint venturer, or agent of Koredor or any Contractor. Installer has full control over all means, methods, personnel, and operations. Koredor has no liability whatsoever for Installer's business operations, decisions, or performance.",
    ],
  },
  {
    title: "3. Tax Status and 1099 Reporting",
    paragraphs: [
      "Installer is solely responsible for all federal, state, and local taxes on amounts received, including self-employment tax. Installer warrants that all tax information provided is accurate. Koredor will issue Form 1099-NEC as required by law. Installer shall indemnify Koredor for any tax, penalty, or reclassification claim.",
    ],
  },
  {
    title: "4. Scope of Work and Dispatch",
    paragraphs: [
      "Koredor may offer Opportunities via the Portal. Installer may only accept Jobs they are fully licensed, insured, qualified, and equipped to perform.",
    ],
  },
  {
    title: "5. Licensing, Insurance, and Compliance",
    paragraphs: [
      "Installer must maintain at all times:",
      "- All required contractor licenses.",
      "- General Liability Insurance with minimum limits of $1,000,000 per occurrence and $2,000,000 aggregate.",
      "- Commercial Auto Insurance with minimum limits of $1,000,000 combined single limit, including owned, non-owned, and hired vehicles used for any Job.",
      "- Workers Compensation insurance where required by law.",
      "- Additional Insured status naming the Contractor on a primary and non-contributory basis when directed, with waiver of subrogation in favor of the Contractor.",
      "- Current W-9.",
      "Installer must provide updated certificates of insurance within 24 hours of request. Any lapse immediately suspends access to new Opportunities. Installer shall maintain completed operations coverage for not less than two (2) years following completion of any Job.",
      "Koredor may, at its discretion, require that it also be named as Additional Insured for specific Opportunities.",
    ],
  },
  {
    title: "6. Background Checks and Screening",
    paragraphs: [
      "Installer consents to background screening during onboarding and at reasonable intervals thereafter. Installer agrees to submit to drug screening when reasonably requested for a specific Job. Failure to comply may disqualify Installer from that Job.",
    ],
  },
  {
    title: "7. Installer Responsibility and Assumption of Risk",
    paragraphs: [
      "Installer installs at its own risk and assumes full responsibility and liability for all Installer-Related Issues to the fullest extent permitted by law. Installer is solely responsible for means and methods of construction, jobsite safety, personnel supervision, workmanship, compliance, vehicles, equipment, materials, scheduling, and quality control.",
      "Koredor operates solely as a technology platform and does not supervise, direct, inspect, control, or guarantee Installer's work. To the fullest extent permitted by law, Koredor and the Contractor have no liability for any Installer-Related Issue, including any injury, death, or damage on or related to the Job site or travel to or from the Job site.",
      "Installer provides a minimum one (1) year craftsmanship warranty on all workmanship and installation from the date of completion (or longer if required by law or Job specifications). This warranty does not extend to defects in materials supplied by others.",
    ],
  },
  {
    title: "8. Subcontractor Flow-Down",
    paragraphs: [
      "Installer is fully responsible for all subcontractors and must flow down all material terms of this Agreement to them in writing. Installer's liability is not reduced by any subcontractor's actions or failures.",
    ],
  },
  {
    title: "9. Indemnification",
    paragraphs: [
      "To the fullest extent permitted by law, Installer shall defend, indemnify, and hold harmless Koredor, the Contractor, Customers, and their officers, directors, employees, agents, and affiliates from any and all claims, damages, losses, liabilities, costs, and expenses (including attorneys fees) arising from any Installer-Related Issue or Installer's performance or non-performance under this Agreement.",
      "This indemnification obligation is unlimited in amount and survives termination.",
    ],
  },
  {
    title: "10. Insurance Coordination",
    paragraphs: [
      "Installer's insurance shall be primary and non-contributing. If Installer's insurance fails to cover any claim, Installer remains personally and fully liable for the entire amount.",
    ],
  },
  {
    title: "10A. Audit Rights",
    paragraphs: [
      "Koredor or the Contractor may request documentation proving compliance with this Agreement. Installer must provide all requested documents within 72 hours or face suspension.",
    ],
  },
  {
    title: "11. Mechanic's Lien Waivers",
    paragraphs: [
      "Upon completion of each Job and prior to payment, Installer must provide unconditional lien waivers from itself and all subcontractors and suppliers. Koredor may withhold payment until all required unconditional lien waivers are received in a form satisfactory to Koredor.",
    ],
  },
  {
    title: "12. Customer and Jobsite Standards",
    paragraphs: [
      "Installer must maintain professionalism, protect all property, keep the site clean, and communicate promptly. Installer may not solicit direct work from Customers or Contractors outside the Koredor platform.",
    ],
  },
  {
    title: "13. Confidentiality and Customer Data",
    paragraphs: [
      "Installer must keep all Customer and Contractor information confidential and use it only to perform the Job. Installer must notify Koredor within 24 hours of any suspected data breach.",
    ],
  },
  {
    title: "14. Photo and Content License",
    paragraphs: [
      "Installer grants Koredor and the Contractor a perpetual, royalty-free license to use any photos, videos, or content uploaded to the Portal.",
    ],
  },
  {
    title: "15. Proof of Completion",
    paragraphs: [
      "Before payment, Installer must submit before and after photos, signed Customer sign-off, and lien waivers through the Portal.",
    ],
  },
  {
    title: "16. Payment Terms",
    paragraphs: [
      "Payment is conditional upon full completion and submission of all required proof. Koredor may withhold or issue chargebacks for any Installer-Related Issues.",
    ],
  },
  {
    title: "17. Callbacks, Rework, and Chargebacks",
    paragraphs: [
      "If an Installer-Related Issue arises, Installer must acknowledge notice within 48 hours and cure within a commercially reasonable time. If Installer fails to cure, Koredor or the Contractor may repair or complete the work and recover all costs from Installer, including through chargebacks or direct demand.",
    ],
  },
  {
    title: "17A. Emergency Step-In Rights",
    paragraphs: [
      "If Installer abandons a Job, creates a safety or regulatory issue, or fails to respond to urgent matters, Koredor or the Contractor may immediately step in to secure, repair, or complete the work. Installer remains responsible for all resulting costs and damages.",
    ],
  },
  {
    title: "18. Non-Solicitation and Non-Circumvention",
    paragraphs: [
      "For twelve (12) months after the last Job, Installer shall not directly or indirectly solicit, contract with, or perform work for any Customer or Contractor introduced through Koredor outside the platform. Liquidated damages shall equal the greater of $5,000 per violation or the total compensation Installer would have earned through Koredor on the diverted work. Injunctive relief is also available.",
    ],
  },
  {
    title: "19. Limitation of Liability",
    paragraphs: [
      "To the maximum extent permitted by law, Koredor's total liability shall not exceed the amount paid to Installer in the twelve months preceding the claim. Koredor shall not be liable for indirect, consequential, or punitive damages.",
    ],
  },
  {
    title: "20. Termination",
    paragraphs: [
      "Either party may terminate this Agreement with notice. Termination does not release Installer from liability for prior Jobs, warranty, indemnification, or chargebacks.",
    ],
  },
  {
    title: "21. Governing Law and Dispute Resolution",
    paragraphs: [
      "This Agreement is governed by Florida law. All disputes shall be resolved by binding arbitration in Manatee County, Florida. The prevailing party shall recover attorneys fees and costs.",
    ],
  },
  {
    title: "22. Electronic Signature",
    paragraphs: [
      "Electronic acceptance through the Portal constitutes a binding signature.",
    ],
  },
  {
    title: "23. Miscellaneous",
    paragraphs: [
      "This Agreement is the entire agreement between the parties. Koredor may update this Agreement with reasonable notice through the Portal or by email. Installer's continued participation after such notice constitutes acceptance of the updated terms. If any provision is invalid, the remainder remains in effect. Force Majeure applies.",
    ],
  },
  {
    title: "24. Acknowledgment",
    paragraphs: [
      "By accepting this Agreement, Installer confirms it has read, understood, and agrees to all terms, including full assumption of risk and liability for all Installer-Related Issues and the strong protections provided to Koredor and the Contractor.",
      "Effective version: v1.9-R2. Questions: legal@koredor.com.",
    ],
  },
];

type SectionId =
  | "applicant"
  | "crews"
  | "docs"
  | "canonical_compliance"
  | "company_compliance"
  | "relationship_review"
  | "requirements"
  | "agreement"
  | "decision";

const SECTIONS: Array<{
  id: SectionId;
  label: string;
  icon: React.ElementType;
  canonicalOnly?: boolean;
}> = [
  { id: "applicant", label: "Applicant", icon: User },
  { id: "crews", label: "Crews", icon: ShieldCheck },
  { id: "docs", label: "Compliance docs", icon: FileCheck },
  {
    id: "canonical_compliance",
    label: "Individual Compliance",
    icon: ShieldCheck,
    canonicalOnly: true,
  },
  {
    id: "company_compliance",
    label: "Company Compliance",
    icon: FileCheck,
    canonicalOnly: true,
  },
  {
    id: "relationship_review",
    label: "Relationship Review",
    icon: ShieldCheck,
    canonicalOnly: true,
  },
  { id: "requirements", label: "Requirements", icon: ClipboardList },
  { id: "agreement", label: "Agreement & pay", icon: ScrollText },
  { id: "decision", label: "Decision", icon: ShieldCheck },
];

const ALLOWED_DECISION_ROLES = new Set(["super_admin", "admin", "ops_manager", "it_staff"]);

const REQUIREMENT_DOCUMENT_FIELDS: Array<{
  key: keyof OnboardingComplianceRequirementRuleSet;
  label: string;
}> = [
  { key: "licenseRequired", label: "License" },
  { key: "insuranceRequired", label: "Insurance" },
  { key: "w9Required", label: "W-9" },
  { key: "backgroundCheckRequired", label: "Background check" },
  { key: "certificationRequired", label: "Certification" },
];

const REQUIREMENT_INHERIT_FIELDS: Array<{
  key: keyof OnboardingComplianceRequirementRuleSet;
  label: string;
}> = [
  { key: "inheritsLicenseFromParent", label: "License inherited from parent" },
  { key: "inheritsInsuranceFromParent", label: "Insurance inherited from parent" },
  { key: "inheritsW9FromParent", label: "W-9 inherited from parent" },
];

type ComplianceDocumentOwnerGroup = {
  key: string;
  label: string;
  subtitle: string;
  ownerKind: "company" | "crew";
  contractorId: string | null;
  installerUserId: string | null;
  contractorCrewId: string | null;
  contractorCrewStatus: string | null;
  docs: ComplianceDocument[];
};

type ContractorCrewRosterRow = {
  id: string;
  contractorId: string;
  userId: string | null;
  crewRole: string | null;
  status: string | null;
  fullName: string | null;
  email: string | null;
};

type AgreementSignature = NonNullable<OnboardingSession["signatures"]>[number];

function compact(value: unknown): string {
  return String(value ?? "").trim();
}

function normalizeIdentity(value: string | null | undefined): string {
  return compact(value).toLowerCase();
}

function isUuidLike(value: string | null | undefined): boolean {
  const normalized = compact(value).toLowerCase();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(normalized);
}

function realContractorId(value: string | null | undefined): string | null {
  const id = compact(value);
  if (!id || id.startsWith("onboarding:")) return null;
  return id;
}

function resolveCrewActivationIdentifiers(group: ComplianceDocumentOwnerGroup): {
  contractorCrewId?: string;
  crewUserId?: string;
} {
  const contractorCrewId = isUuidLike(group.contractorCrewId) ? compact(group.contractorCrewId) : "";
  const crewUserId = compact(group.installerUserId);
  return {
    contractorCrewId: contractorCrewId || undefined,
    crewUserId: crewUserId || undefined,
  };
}

function crewGroupMatchesRosterRow(
  group: ComplianceDocumentOwnerGroup,
  crew: ContractorCrewRosterRow,
): boolean {
  if (group.ownerKind !== "crew") return false;
  const groupContractorId = realContractorId(group.contractorId);
  const crewContractorId = realContractorId(crew.contractorId);
  if (groupContractorId && crewContractorId && groupContractorId !== crewContractorId) return false;
  if (group.contractorCrewId && group.contractorCrewId === crew.id) return true;
  if (group.installerUserId && crew.userId && group.installerUserId === crew.userId) return true;

  const crewEmail = normalizeIdentity(crew.email);
  const crewName = normalizeIdentity(crew.fullName);
  const groupLabel = normalizeIdentity(group.label.replace(/^crew:\s*/i, ""));
  const groupSubtitle = normalizeIdentity(group.subtitle);

  if (crewEmail && (groupLabel === crewEmail || groupSubtitle.includes(crewEmail))) return true;
  if (crewName && groupLabel === crewName) return true;

  return group.docs.some((doc) => {
    const docCrewId = normalizeIdentity(readDocMetaString(doc, "contractorCrewId"));
    const docUserId = normalizeIdentity(readDocMetaString(doc, "installerUserId"));
    const docEmail = normalizeIdentity(readDocMetaString(doc, "installerEmail"));
    const docName =
      normalizeIdentity(readDocMetaString(doc, "installerFullName")) ||
      normalizeIdentity(readDocMetaString(doc, "installerCrewName"));

    return (
      (docCrewId && docCrewId === normalizeIdentity(crew.id)) ||
      (docUserId && crew.userId && docUserId === normalizeIdentity(crew.userId)) ||
      (docEmail && crewEmail && docEmail === crewEmail) ||
      (docName && crewName && docName === crewName)
    );
  });
}

function toStringArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((item) => compact(item)).filter(Boolean);
  if (typeof value === "string") {
    return value.split(",").map((part) => part.trim()).filter(Boolean);
  }
  return [];
}

function draftCrewRows(
  session: OnboardingSession | null,
  contractor: Contractor | null,
  allSessions: OnboardingSession[] = [],
): ContractorCrewRosterRow[] {
  if (!session) return [];

  const draft = getDraftRecord(session);
  const parent = (draft.parent && typeof draft.parent === "object") ? draft.parent as Record<string, unknown> : null;
  const parentIdentity = parent?.identity && typeof parent.identity === "object"
    ? parent.identity as Record<string, unknown>
    : null;
  const rootCrew = (draft.crew && typeof draft.crew === "object") ? draft.crew as Record<string, unknown> : null;
  const parentCrew = (parent?.crew && typeof parent.crew === "object") ? parent.crew as Record<string, unknown> : null;
  const crewNode = rootCrew ?? parentCrew;
  const invitees = Array.isArray(crewNode?.invitees) ? crewNode?.invitees as Array<Record<string, unknown>> : [];

  const contractorId =
    realContractorId(session.contractorId) ??
    realContractorId(session.parentEntityId) ??
    realContractorId(contractor?.id) ??
    "draft-session";

  const rows: ContractorCrewRosterRow[] = [];
  const rootSessionId = compact(session.id);
  const ownerName = compact(draft.contactName) || compact(parentIdentity?.contactName) || compact(draft.name);
  const ownerEmail = compact(draft.contactEmail) || compact(parentIdentity?.contactEmail) || compact(contractor?.email);

  if (ownerName || ownerEmail) {
    rows.push({
      id: `draft-owner:${session.id}`,
      contractorId,
      userId: null,
      crewRole: "owner",
      status: "active",
      fullName: ownerName || null,
      email: ownerEmail || null,
    });
  }

  invitees.forEach((invitee, index) => {
    const firstName = compact(invitee.firstName);
    const lastName = compact(invitee.lastName);
    const fullName = [firstName, lastName].filter(Boolean).join(" ").trim() || null;
    const email = compact(invitee.email) || null;
    const status = compact(invitee.status) || "invited";
    rows.push({
      id: `draft-invitee:${session.id}:${index}:${email ?? fullName ?? "crew"}`,
      contractorId,
      userId: null,
      crewRole: compact(invitee.orgRole) || "installer",
      status,
      fullName,
      email,
    });
  });

  const parentContractorId =
    realContractorId(session.contractorId) ??
    realContractorId(session.parentEntityId) ??
    realContractorId(contractor?.id);

  const childCrewSessions = allSessions.filter((candidate) => {
    if (candidate.id === session.id) return false;
    if (!isCrewScopedEntity(candidate.entityType)) return false;
    const candidateParentId =
      realContractorId(candidate.parentEntityId) ??
      realContractorId(candidate.contractorId);
    const directParentSessionMatch =
      rootSessionId.length > 0 &&
      compact(candidate.parentEntityId) === rootSessionId;
    const directContractorMatch =
      Boolean(parentContractorId && candidateParentId === parentContractorId);
    const candidateDraft = (candidate.draft && typeof candidate.draft === "object")
      ? candidate.draft as Record<string, unknown>
      : null;
    const candidateDraftParentId = compact(candidateDraft?.parentEntityId);
    const draftParentSessionMatch =
      rootSessionId.length > 0 && candidateDraftParentId === rootSessionId;
    return directParentSessionMatch || directContractorMatch || draftParentSessionMatch;
  });

  childCrewSessions.forEach((child) => {
    const childDraft = child.draft as Record<string, unknown>;
    const fullName =
      compact(childDraft.contactName) ||
      compact(childDraft.name) ||
      null;
    const email = compact(childDraft.contactEmail) || null;
    const derivedRole =
      child.entityType === "helper"
        ? "helper"
        : compact(childDraft.orgRole) || "crew";
    rows.push({
      id: `draft-child-session:${child.id}`,
      contractorId,
      userId: null,
      crewRole: derivedRole,
      status: compact(child.status) || "invited",
      fullName,
      email,
    });
  });

  return rows;
}

function inviteTokenCrewRows(
  session: OnboardingSession | null,
  contractor: Contractor | null,
  inviteTokens: OnboardingInviteToken[] = [],
  allSessions: OnboardingSession[] = [],
): ContractorCrewRosterRow[] {
  if (!session) return [];

  const contractorId =
    realContractorId(session.contractorId) ??
    realContractorId(session.parentEntityId) ??
    realContractorId(contractor?.id);
  const rootSessionId = compact(session.id);
  if (!contractorId) return [];

  const consumedTokenIds = new Set(allSessions.map((candidate) => compact(candidate.tokenId)).filter(Boolean));

  return inviteTokens
    .filter((token) => {
      if (!isCrewScopedEntity(token.entityType)) return false;
      if (token.status === "revoked") return false;
      if (consumedTokenIds.has(compact(token.id))) return false;

      const tokenContractorId = realContractorId(token.contractorId);
      const tokenParentId = compact(token.parentId);
      const parentMatchesSession =
        rootSessionId.length > 0 && tokenParentId === rootSessionId;
      const parentMatchesContractor =
        tokenParentId.length > 0 && tokenParentId === contractorId;
      const contractorMatches =
        Boolean(tokenContractorId && tokenContractorId === contractorId);

      return contractorMatches || parentMatchesSession || parentMatchesContractor;
    })
    .map((token) => ({
      id: `draft-token:${token.id}`,
      contractorId,
      userId: null,
      crewRole: token.entityType === "helper" ? "helper" : "crew",
      status: token.status || "pending",
      fullName: `${token.entityType === "helper" ? "Helper" : "Crew"} invite · ${token.id.slice(-6)}`,
      email: null,
    }));
}

function mergeCrewRosterRows(
  liveRows: ContractorCrewRosterRow[],
  draftRows: ContractorCrewRosterRow[],
): ContractorCrewRosterRow[] {
  const merged = new Map<string, ContractorCrewRosterRow>();
  const preferLiveRow = (left: ContractorCrewRosterRow, right: ContractorCrewRosterRow) => {
    const leftIsLive = !left.id.startsWith("draft-");
    const rightIsLive = !right.id.startsWith("draft-");
    if (leftIsLive !== rightIsLive) return leftIsLive ? left : right;
    const leftHasUser = Boolean(left.userId);
    const rightHasUser = Boolean(right.userId);
    if (leftHasUser !== rightHasUser) return leftHasUser ? left : right;
    return left;
  };
  const makeKey = (row: ContractorCrewRosterRow) => {
    const email = compact(row.email).toLowerCase();
    if (email) return `email:${email}`;
    const fullName = compact(row.fullName).toLowerCase();
    if (fullName) return `name:${fullName}`;
    const userId = compact(row.userId).toLowerCase();
    if (userId) return `user:${userId}`;
    return `id:${row.id}`;
  };

  [...liveRows, ...draftRows].forEach((row) => {
    const key = makeKey(row);
    const existing = merged.get(key);
    if (!existing) {
      merged.set(key, row);
      return;
    }
    const primary = preferLiveRow(existing, row);
    const secondary = primary === existing ? row : existing;
    merged.set(key, {
      ...secondary,
      ...primary,
      id: primary.id,
      contractorId: primary.contractorId || secondary.contractorId,
      userId: primary.userId ?? secondary.userId,
      crewRole: primary.crewRole ?? secondary.crewRole,
      status: primary.status ?? secondary.status,
      fullName: primary.fullName ?? secondary.fullName,
      email: primary.email ?? secondary.email,
    });
  });

  return Array.from(merged.values()).sort((a, b) => {
    const aStatus = crewStatusLabel(a.status);
    const bStatus = crewStatusLabel(b.status);
    if (aStatus !== bStatus) return aStatus.localeCompare(bStatus);
    return (a.fullName ?? a.email ?? a.id).localeCompare(b.fullName ?? b.email ?? b.id);
  });
}

function buildCrewRosterRowsFromDocuments(
  docs: ComplianceDocument[],
  contractor: Contractor | null,
): ContractorCrewRosterRow[] {
  const merged = new Map<string, ContractorCrewRosterRow>();

  for (const doc of docs) {
    if (getComplianceDocumentOwnerKind(doc) !== "crew") continue;

    const contractorCrewId = readDocMetaString(doc, "contractorCrewId");
    const installerUserId = readDocMetaString(doc, "installerUserId");
    const installerEmail = readDocMetaString(doc, "installerEmail");
    const installerFullName = readDocMetaString(doc, "installerFullName");
    const installerCrewName = readDocMetaString(doc, "installerCrewName");
    const contractorCrewStatus = readDocMetaString(doc, "contractorCrewStatus");
    const contractorCrewRole = readDocMetaString(doc, "contractorCrewRole");

    const fullName = installerFullName || installerCrewName || null;
    const email = installerEmail || null;
    const key =
      contractorCrewId ||
      installerUserId ||
      normalizeIdentity(email) ||
      normalizeIdentity(fullName) ||
      `doc-crew:${doc.id}`;

    const nextRow: ContractorCrewRosterRow = {
      id: contractorCrewId || `doc-crew:${key}`,
      contractorId: doc.contractorId || contractor?.id || "doc-session",
      userId: installerUserId || null,
      crewRole: contractorCrewRole || null,
      status: contractorCrewStatus || null,
      fullName,
      email,
    };

    const existing = merged.get(key);
    if (!existing) {
      merged.set(key, nextRow);
      continue;
    }

    merged.set(key, {
      ...existing,
      id: existing.id.startsWith("doc-crew:") && contractorCrewId ? contractorCrewId : existing.id,
      contractorId: existing.contractorId || nextRow.contractorId,
      userId: existing.userId ?? nextRow.userId,
      crewRole: existing.crewRole ?? nextRow.crewRole,
      status: existing.status ?? nextRow.status,
      fullName: existing.fullName ?? nextRow.fullName,
      email: existing.email ?? nextRow.email,
    });
  }

  return Array.from(merged.values());
}

function getDraftRecord(session: OnboardingSession): Record<string, unknown> {
  return session.draft as unknown as Record<string, unknown>;
}

function getSessionFootprint(session: OnboardingSession): Record<string, unknown> | undefined {
  const parent = getDraftRecord(session).parent as Record<string, unknown> | undefined;
  return parent?.footprint as Record<string, unknown> | undefined;
}

function isCrewScopedEntity(entityType: string | null | undefined): boolean {
  return entityType === "crew" || entityType === "helper";
}

function buildRequirementRuleSet(
  reqs: ComplianceRequirements | null,
): OnboardingComplianceRequirementRuleSet {
  if (!reqs) return {};
  return {
    licenseRequired: reqs.licenseRequired,
    insuranceRequired: reqs.insuranceRequired,
    w9Required: reqs.w9Required,
    backgroundCheckRequired: reqs.backgroundCheckRequired,
    certificationRequired: reqs.agreementVariant === "certification_agreement",
    inheritsLicenseFromParent: reqs.inheritsFromParent.license,
    inheritsInsuranceFromParent: reqs.inheritsFromParent.insurance,
    inheritsW9FromParent: reqs.inheritsFromParent.w9,
  };
}

function requirementRuleValue(
  ruleSet: OnboardingComplianceRequirementRuleSet,
  key: keyof OnboardingComplianceRequirementRuleSet,
): boolean {
  return Boolean(ruleSet[key]);
}

function buildCompleteRequirementRuleSet(
  ruleSet: OnboardingComplianceRequirementRuleSet | undefined,
): OnboardingComplianceRequirementRuleSet {
  const complete: OnboardingComplianceRequirementRuleSet = {};
  for (const field of REQUIREMENT_DOCUMENT_FIELDS) {
    complete[field.key] = Boolean(ruleSet?.[field.key]);
  }
  for (const field of REQUIREMENT_INHERIT_FIELDS) {
    complete[field.key] = Boolean(ruleSet?.[field.key]);
  }
  return complete;
}

function getSessionTradeLabel(session: OnboardingSession, contractor: Contractor | null): string {
  const tradeValues = toStringArray(getDraftRecord(session).tradeType);
  return compact(contractor?.tradeType) || tradeValues.join(", ");
}

function getSessionServiceRegions(session: OnboardingSession, contractor: Contractor | null): string[] {
  const draft = getDraftRecord(session);
  const footprint = getSessionFootprint(session);
  return [
    ...(contractor?.serviceRegions ?? []),
    ...toStringArray(draft.serviceRegions),
    ...toStringArray(draft.serviceAreas),
    ...toStringArray(footprint?.serviceAreas),
  ].filter((value, index, all) => value && all.indexOf(value) === index);
}

function getSessionServiceZipCodes(session: OnboardingSession, contractor: Contractor | null): string[] {
  const draft = getDraftRecord(session);
  const footprint = getSessionFootprint(session);
  return [
    ...(contractor?.serviceZipCodes ?? []),
    ...toStringArray(draft.serviceZipCodes),
    ...toStringArray(footprint?.zips).filter((value) => /^\d{5}(?:-\d{4})?$/.test(value)),
  ].filter((value, index, all) => value && all.indexOf(value) === index);
}

function getSessionServiceAreaLabel(session: OnboardingSession, contractor: Contractor | null): string {
  const draft = getDraftRecord(session);
  const footprint = getSessionFootprint(session);
  const regions = getSessionServiceRegions(session, contractor);
  const zips = getSessionServiceZipCodes(session, contractor);
  return (
    compact(contractor?.serviceArea) ||
    compact(draft.serviceAreas) ||
    compact(footprint?.serviceAreas) ||
    regions.join(", ") ||
    zips.join(", ")
  );
}

function parseWeeklyCapacity(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) return Math.round(value);
  const numeric = compact(value).match(/\d+(?:\.\d+)?/g)?.map(Number).filter((n) => Number.isFinite(n) && n > 0);
  return numeric?.length ? Math.ceil(Math.max(...numeric)) : null;
}

function getSessionMaxWeeklyJobs(session: OnboardingSession, contractor: Contractor | null): number | null {
  if (contractor?.maxWeeklyJobs && contractor.maxWeeklyJobs > 0) return contractor.maxWeeklyJobs;
  const draft = getDraftRecord(session);
  const footprint = getSessionFootprint(session);
  return (
    parseWeeklyCapacity(draft.maxWeeklyJobs) ??
    parseWeeklyCapacity(draft.weeklyCapacity) ??
    parseWeeklyCapacity(draft.capacity) ??
    parseWeeklyCapacity(footprint?.capacity)
  );
}

function deriveSpecArchetypesFromOnboarding(
  session: OnboardingSession,
  contractor: Contractor | null,
): SpecArchetype[] {
  const text = [getSessionTradeLabel(session, contractor), ...toStringArray(getDraftRecord(session).tradeType)]
    .join(" ")
    .toLowerCase();
  const specs = new Set<SpecArchetype>();
  if (text.includes("bath")) specs.add("FULL-BATH");
  if (text.includes("shower")) specs.add("SHWR-RPL");
  if (text.includes("tub-to-shower") || text.includes("tub to shower")) specs.add("TTS-STD");
  if (text.includes("tub") && !specs.has("TTS-STD")) specs.add("TUB-RPL");
  if (text.includes("walk-in") || text.includes("walk in")) specs.add("WALK-IN-TUB");
  if (text.includes("wet")) specs.add("WET-SPACE");
  if (text.includes("window")) specs.add("WINDOW");
  if (text.includes("floor")) specs.add("FLOORING");
  return Array.from(specs);
}

function buildPayProfileFromOnboarding(
  contractorId: string,
  session: OnboardingSession,
  contractor: Contractor | null,
): ContractorPayProfile | null {
  const allowedSpecTypes = deriveSpecArchetypesFromOnboarding(session, contractor);
  if (allowedSpecTypes.length === 0) return null;
  return {
    ...defaultPayProfile(contractorId),
    id: `PP-${contractorId.slice(0, 8)}`,
    allowedSpecTypes,
    notes: "Created from approved onboarding application.",
  };
}

function getInstallerPortalInviteRedirect(): string {
  const configuredBase = String(
    import.meta.env.VITE_INSTALLER_PORTAL_BASE_URL ??
      import.meta.env.VITE_INSTALLER_PORTAL_URL ??
      "",
  ).trim();
  const base = configuredBase || DEFAULT_INSTALLER_PORTAL_URL;
  return `${base.replace(/\/+$/, "")}/activate`;
}

function resolveInviteIdentity(session: OnboardingSession | null): { email: string; fullName: string } | null {
  if (!session) return null;
  const email = compact(session.draft.contactEmail).toLowerCase();
  if (!email) return null;
  const fullName = compact(session.draft.contactName ?? session.draft.name) || "Installer Applicant";
  return { email, fullName };
}

function getSessionAgreementSignatures(session: OnboardingSession): AgreementSignature[] {
  const signaturesById = new Map<string, AgreementSignature>();
  const draftSignatures = Array.isArray(session.draft?.signatures)
    ? (session.draft.signatures as AgreementSignature[])
    : [];
  for (const sig of [...(session.signatures ?? []), ...draftSignatures]) {
    if (sig?.id) signaturesById.set(sig.id, sig);
  }
  return Array.from(signaturesById.values()).sort((a, b) => Date.parse(b.signedAt) - Date.parse(a.signedAt));
}

function isWebsiteComplianceDocument(doc: ComplianceDocument): boolean {
  return doc.metadata?.source === "installer_compliance_documents";
}

function getComplianceDocumentStoragePath(doc: ComplianceDocument): string {
  return String(doc.metadata?.storagePath ?? doc.url ?? "").trim();
}

function getComplianceDocumentFileName(doc: ComplianceDocument): string {
  return String(doc.fileName ?? doc.metadata?.fileName ?? doc.metadata?.file_name ?? doc.type).trim();
}

function getComplianceDocumentMimeType(doc: ComplianceDocument): string {
  return String(doc.metadata?.mimeType ?? doc.metadata?.mime_type ?? "").trim().toLowerCase();
}

function getComplianceDocumentPreviewKind(doc: ComplianceDocument, href: string): "image" | "pdf" | "other" {
  const mimeType = getComplianceDocumentMimeType(doc);
  const fileName = getComplianceDocumentFileName(doc).toLowerCase();
  const urlPath = href.split("?")[0]?.toLowerCase() ?? "";
  if (mimeType.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(fileName || urlPath)) return "image";
  if (mimeType === "application/pdf" || /\.pdf$/.test(fileName || urlPath)) return "pdf";
  return "other";
}

function readDocMetaString(doc: ComplianceDocument, key: string): string {
  const value = doc.metadata?.[key];
  return typeof value === "string" ? value.trim() : "";
}

function getComplianceDocumentOwnerKind(doc: ComplianceDocument): "company" | "crew" {
  const rawType = String(doc.metadata?.rawType ?? doc.type ?? "").trim().toLowerCase();
  const crewRole = readDocMetaString(doc, "contractorCrewRole").toLowerCase();
  const installerUserId = readDocMetaString(doc, "installerUserId");
  const contractorCrewId = readDocMetaString(doc, "contractorCrewId");
  const installerEmail = readDocMetaString(doc, "installerEmail");
  const installerFullName = readDocMetaString(doc, "installerFullName");
  const installerCrewName = readDocMetaString(doc, "installerCrewName");

  if (["background_check", "driver_license", "osha_10", "drug_screen", "manufacturer_cert"].includes(rawType)) {
    return "crew";
  }

  if (
    ["installer", "crew", "helper"].includes(crewRole) &&
    (installerUserId || contractorCrewId || installerEmail || installerFullName || installerCrewName)
  ) {
    return "crew";
  }

  return "company";
}

function groupComplianceDocumentsByOwner(
  docs: ComplianceDocument[],
  contractor?: Contractor | null,
): ComplianceDocumentOwnerGroup[] {
  const groups = new Map<string, ComplianceDocumentOwnerGroup>();
  for (const doc of docs) {
    const ownerKind = getComplianceDocumentOwnerKind(doc);
    const crewName = readDocMetaString(doc, "installerCrewName");
    const installerName = readDocMetaString(doc, "installerFullName");
    const installerEmail = readDocMetaString(doc, "installerEmail");
    const installerUserId = readDocMetaString(doc, "installerUserId");
    const contractorCrewId = readDocMetaString(doc, "contractorCrewId");
    const contractorCrewStatus = readDocMetaString(doc, "contractorCrewStatus");
    const contractorName = readDocMetaString(doc, "contractorCompanyName") || contractor?.companyName || "Company";
    const ownerLabel =
      ownerKind === "crew"
        ? crewName || installerName || installerEmail || (installerUserId ? `Crew ${installerUserId.slice(0, 8)}` : "Crew member")
        : contractorName;
    const key = `${ownerKind}:${ownerKind === "crew" ? installerUserId || ownerLabel : doc.contractorId || ownerLabel}`;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        label: `${ownerKind === "crew" ? "Crew" : "Company"}: ${ownerLabel}`,
        subtitle:
          ownerKind === "crew"
            ? [installerEmail && installerEmail !== ownerLabel ? installerEmail : null, contractorName ? `Contractor: ${contractorName}` : null]
                .filter(Boolean)
                .join(" / ") || "Crew documents"
            : "Company documents",
        ownerKind,
        contractorId: doc.contractorId || contractor?.id || null,
        installerUserId: installerUserId || null,
        contractorCrewId: contractorCrewId || null,
        contractorCrewStatus: contractorCrewStatus || null,
        docs: [],
      });
    }
    groups.get(key)?.docs.push(doc);
  }
  return Array.from(groups.values()).sort((a, b) => a.label.localeCompare(b.label));
}

function metadataString(doc: ComplianceDocument, key: string): string {
  const value = doc.metadata?.[key];
  return typeof value === "string" ? value.trim() : "";
}

function normalizeAgreementToken(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function scoreSignedAgreementDocument(
  doc: ComplianceDocument,
  variant: AgreementVariant | null,
  signature: AgreementSignature | null,
): number {
  const fileName = getComplianceDocumentFileName(doc).toLowerCase();
  const rawType = normalizeAgreementToken(doc.metadata?.rawType ?? doc.type);
  const storagePath = getComplianceDocumentStoragePath(doc).toLowerCase();
  const haystack = `${fileName} ${rawType} ${storagePath}`;
  const variantToken = normalizeAgreementToken(variant);
  const signatureId = String(signature?.id ?? "").trim().toLowerCase();

  let score = 0;
  if (doc.type === "agreement") score += 25;
  if (variantToken && haystack.includes(variantToken)) score += 80;
  if (haystack.includes("signed")) score += 70;
  if (haystack.includes("msa")) score += 60;
  if (haystack.includes("agreement") || haystack.includes("contract")) score += 35;
  if (haystack.includes(".pdf") || getComplianceDocumentMimeType(doc) === "application/pdf") score += 30;
  if (signatureId && haystack.includes(signatureId)) score += 100;
  if (doc.verifiedAt) score += 5;
  if (doc.rejectedAt) score -= 50;
  return score;
}

function getSignedAgreementDocument(
  docs: ComplianceDocument[],
  variant: AgreementVariant | null,
  signature: AgreementSignature | null,
): ComplianceDocument | null {
  const ranked = docs
    .map((doc) => ({ doc, score: scoreSignedAgreementDocument(doc, variant, signature) }))
    .filter(({ score }) => score >= 100)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return Date.parse(b.doc.uploadedAt) - Date.parse(a.doc.uploadedAt);
    });
  return ranked[0]?.doc ?? null;
}

function getReviewerDocuments(
  documents: ComplianceDocument[],
  session: OnboardingSession,
  contractor: Contractor | null,
): ComplianceDocument[] {
  const ids = new Set(
    [
      onboardingContractorId(session.id),
      session.id,
      session.contractorId,
      contractor?.id,
    ]
      .map((value) => String(value ?? "").trim())
      .filter(Boolean),
  );

  return documents
    .filter((doc) => {
      if (doc.supersededBy) return false;
      if (ids.has(doc.contractorId)) return true;
      if (ids.has(metadataString(doc, "sessionId"))) return true;
      if (ids.has(metadataString(doc, "onboardingSessionId"))) return true;
      if (ids.has(metadataString(doc, "contractorId"))) return true;
      if (ids.has(metadataString(doc, "contractor_id"))) return true;

      const orgId = String(contractor?.organizationId ?? "").trim();
      const companyName = String(contractor?.companyName ?? "").trim().toLowerCase();
      const docOrgId = metadataString(doc, "organizationId");
      const docCompanyName = metadataString(doc, "contractorCompanyName").toLowerCase();
      return Boolean(orgId && companyName && docOrgId === orgId && docCompanyName === companyName);
    })
    .sort((a, b) => Date.parse(b.uploadedAt) - Date.parse(a.uploadedAt));
}

function summarizeDocuments(docs: ComplianceDocument[]) {
  return docs.reduce(
    (acc, doc) => {
      acc.total += 1;
      if (doc.rejectedAt) acc.rejected += 1;
      else if (doc.verifiedAt) acc.verified += 1;
      else acc.pending += 1;
      return acc;
    },
    { verified: 0, pending: 0, rejected: 0, total: 0 },
  );
}

function getCrewActivationKey(group: ComplianceDocumentOwnerGroup): string {
  return group.contractorCrewId || `${group.contractorId ?? "unknown"}:${group.installerUserId ?? group.key}`;
}

function isCrewGroupActive(group: ComplianceDocumentOwnerGroup, activatedCrewKeys: Set<string>): boolean {
  return group.ownerKind === "crew" && (group.contractorCrewStatus === "active" || activatedCrewKeys.has(getCrewActivationKey(group)));
}

function canActivateCrewGroup(group: ComplianceDocumentOwnerGroup, activatedCrewKeys: Set<string>): boolean {
  if (group.ownerKind !== "crew" || isCrewGroupActive(group, activatedCrewKeys)) return false;
  if (!group.contractorId || (!group.contractorCrewId && !group.installerUserId)) return false;
  return group.docs.length > 0 && group.docs.every((doc) => !!doc.verifiedAt && !doc.rejectedAt);
}

function canBypassCrewActivationGroup(group: ComplianceDocumentOwnerGroup, activatedCrewKeys: Set<string>): boolean {
  if (group.ownerKind !== "crew" || isCrewGroupActive(group, activatedCrewKeys)) return false;
  if (!group.contractorId || (!group.contractorCrewId && !group.installerUserId)) return false;
  return true;
}

async function loadContractorCrewRoster(contractorIds: string[]): Promise<ContractorCrewRosterRow[]> {
  const normalizedIds = Array.from(new Set(contractorIds.map(realContractorId).filter((value): value is string => Boolean(value))));
  if (normalizedIds.length === 0) return [];

  const normalizedFromFunction = (crewRows: InstallerComplianceCrewRosterRow[]) =>
    crewRows
      .map((row) => ({
        id: String(row.id ?? "").trim(),
        contractorId: String(row.contractor_id ?? normalizedIds[0] ?? "").trim(),
        userId: String(row.user_id ?? row.linked_user_id ?? "").trim() || null,
        crewRole: String(row.crew_role ?? "").trim() || null,
        status: String(row.status ?? "").trim() || null,
        fullName: String(row.full_name ?? "").trim() || null,
        email: String(row.email ?? "").trim() || null,
      }))
      .filter((row) => row.id);

  let normalizedCrewRows: ContractorCrewRosterRow[] = [];
  try {
    const crewRows = await listInstallerComplianceCrewRoster(normalizedIds);
    normalizedCrewRows = normalizedFromFunction(crewRows as InstallerComplianceCrewRosterRow[]);
  } catch (error) {
    console.warn("UnifiedReviewerPanel.listInstallerComplianceCrewRoster fallback:", error);
  }

  if (normalizedCrewRows.length === 0) {
    try {
      const crewSelects = [
        "id, user_id, linked_user_id, contractor_id, status, crew_role",
        "id, user_id, contractor_id, status, crew_role",
        "id, linked_user_id, contractor_id, status, crew_role",
      ];
      let fallbackCrews: unknown[] = [];
      let fallbackError: unknown = null;

      for (const select of crewSelects) {
        const { data, error } = await supabase
          .from("contractor_crews")
          .select(select)
          .in("contractor_id", normalizedIds);

        if (!error) {
          fallbackCrews = data ?? [];
          fallbackError = null;
          break;
        }
        fallbackError = error;
      }

      if (fallbackError) throw fallbackError;

      const fallbackRows: ContractorCrewRosterRow[] = (fallbackCrews as Array<{
        id?: string | null;
        user_id?: string | null;
        linked_user_id?: string | null;
        contractor_id?: string | null;
        status?: string | null;
        crew_role?: string | null;
      }>)
        .map((row) => ({
          id: String(row.id ?? "").trim(),
          contractorId: String(row.contractor_id ?? "").trim(),
          userId: String(row.user_id ?? row.linked_user_id ?? "").trim() || null,
          crewRole: String(row.crew_role ?? "").trim() || null,
          status: String(row.status ?? "").trim() || null,
          fullName: null,
          email: null,
        }))
        .filter((row) => row.id && row.contractorId && row.status !== "released" && row.status !== "suspended");

      normalizedCrewRows = mergeCrewRosterRows(normalizedCrewRows, fallbackRows);
    } catch (error) {
      console.warn("UnifiedReviewerPanel.loadContractorCrewRoster direct fallback:", error);
    }
  }

  const unresolvedProfileIds = Array.from(
    new Set(
      normalizedCrewRows
        .filter((row) => row.userId && (!row.fullName || !row.email))
        .map((row) => row.userId)
        .filter((value): value is string => Boolean(value)),
    ),
  );

  if (unresolvedProfileIds.length === 0) {
    return normalizedCrewRows;
  }

  const profileMap = new Map<string, { fullName: string | null; email: string | null }>();
  const profileQueries = await Promise.allSettled([
    supabase
      .from("profiles")
      .select("id, auth_user_id, full_name, first_name, last_name, email")
      .in("id", unresolvedProfileIds),
    supabase
      .from("profiles")
      .select("id, auth_user_id, full_name, first_name, last_name, email")
      .in("auth_user_id", unresolvedProfileIds),
  ]);

  for (const query of profileQueries) {
    if (query.status !== "fulfilled") {
      console.warn("UnifiedReviewerPanel.loadContractorCrewRoster profile lookup failed:", query.reason);
      continue;
    }
    if (query.value.error) {
      console.warn("UnifiedReviewerPanel.loadContractorCrewRoster profile lookup error:", query.value.error);
      continue;
    }

    for (const row of ((query.value.data ?? []) as Array<{
      id?: string | null;
      auth_user_id?: string | null;
      full_name?: string | null;
      first_name?: string | null;
      last_name?: string | null;
      email?: string | null;
    }>)) {
      const profile = buildProfileLabel(row);
      const id = String(row.id ?? "").trim();
      const authUserId = String(row.auth_user_id ?? "").trim();
      if (id) profileMap.set(id, profile);
      if (authUserId) profileMap.set(authUserId, profile);
    }
  }

  return normalizedCrewRows.map((row) => {
    const profile = row.userId ? profileMap.get(row.userId) : null;
    return {
      ...row,
      fullName: row.fullName ?? profile?.fullName ?? null,
      email: row.email ?? profile?.email ?? null,
    };
  });
}

async function resolveContractorIdsByName(contractorNames: string[]): Promise<string[]> {
  const normalizedNames = Array.from(new Set(contractorNames.map(compact).filter(Boolean)));
  if (normalizedNames.length === 0) return [];

  const resolvedIds = new Set<string>();

  const lookups = await Promise.allSettled(
    normalizedNames.map(async (name) => {
      const { data, error } = await supabase
        .from("contractors")
        .select("id, company_name, name")
        .or(`company_name.ilike.${name},name.ilike.${name}`)
        .limit(10);

      if (error) throw error;
      return data ?? [];
    }),
  );

  for (const lookup of lookups) {
    if (lookup.status !== "fulfilled") {
      console.warn("UnifiedReviewerPanel.resolveContractorIdsByName:", lookup.reason);
      continue;
    }
    for (const row of (lookup.value ?? []) as Array<{ id?: string | null }>) {
      const id = realContractorId(row.id);
      if (id) resolvedIds.add(id);
    }
  }

  return Array.from(resolvedIds);
}

function mergeCrewRosterIntoDocumentGroups(
  docGroups: ComplianceDocumentOwnerGroup[],
  crewRoster: ContractorCrewRosterRow[],
  contractor: Contractor | null,
): ComplianceDocumentOwnerGroup[] {
  const merged = new Map<string, ComplianceDocumentOwnerGroup>(
    docGroups.map((group) => [group.key, group]),
  );

  for (const crew of crewRoster) {
    const ownerLabel =
      crew.fullName ||
      crew.email ||
      (crew.userId ? `Crew ${crew.userId.slice(0, 8)}` : `Crew ${crew.id.slice(0, 8)}`);
    const key = `crew:${crew.userId || ownerLabel}`;
    const existingGroup = Array.from(merged.values()).find((group) => crewGroupMatchesRosterRow(group, crew));
    if (existingGroup) {
      existingGroup.contractorId = existingGroup.contractorId || crew.contractorId || contractor?.id || null;
      existingGroup.installerUserId = existingGroup.installerUserId || crew.userId;
      existingGroup.contractorCrewId = existingGroup.contractorCrewId || crew.id;
      existingGroup.contractorCrewStatus = existingGroup.contractorCrewStatus || crew.status;
      existingGroup.subtitle = [
        crew.email && !existingGroup.subtitle.toLowerCase().includes(crew.email.toLowerCase()) ? crew.email : null,
        crew.crewRole && !existingGroup.subtitle.toLowerCase().includes(`role: ${crew.crewRole}`.toLowerCase())
          ? `Role: ${crew.crewRole}`
          : null,
        crew.status && !existingGroup.subtitle.toLowerCase().includes(`status: ${crew.status}`.toLowerCase())
          ? `Status: ${crew.status}`
          : null,
        existingGroup.subtitle,
      ]
        .filter(Boolean)
        .join(" / ");
      continue;
    }
    if (merged.has(key)) continue;
    merged.set(key, {
      key,
      label: `Crew: ${ownerLabel}`,
      subtitle: [
        crew.email && crew.email !== ownerLabel ? crew.email : null,
        crew.crewRole ? `Role: ${crew.crewRole}` : null,
        crew.status ? `Status: ${crew.status}` : null,
        contractor?.companyName ? `Contractor: ${contractor.companyName}` : null,
      ].filter(Boolean).join(" / ") || "Crew documents",
      ownerKind: "crew",
      contractorId: crew.contractorId || contractor?.id || null,
      installerUserId: crew.userId,
      contractorCrewId: crew.id,
      contractorCrewStatus: crew.status,
      docs: [],
    });
  }

  return Array.from(merged.values()).sort((a, b) => {
    const aCompany = a.ownerKind === "company";
    const bCompany = b.ownerKind === "company";
    if (aCompany !== bCompany) return aCompany ? -1 : 1;
    return a.label.localeCompare(b.label);
  });
}

export default function UnifiedReviewerPanel() {
  const [params, setParams] = useSearchParams();
  const sessionId = params.get("session");
  const tokenId = params.get("token");
  const onboarding = useOnboarding();
  const { contractors, setContractors, setPayProfiles } = useAppData();
  const { role } = useRole();
  const { hasPermission: hasPerm } = usePermissions();
  const { toast } = useToast();
  const canonicalInternalReviewEnabled =
    isCanonicalInternalComplianceReviewEnabled();

  const [section, setSection] = useState<SectionId>("applicant");
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reuploadOpen, setReuploadOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [reuploadSteps, setReuploadSteps] = useState<string[]>([]);
  const [rejectTarget, setRejectTarget] = useState<{ contractorId: string; documentId: string } | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [documentPreview, setDocumentPreview] = useState<{ doc: ComplianceDocument; href: string } | null>(null);
  const [agreementPreviewOpen, setAgreementPreviewOpen] = useState(false);
  const [activatedCrewKeys, setActivatedCrewKeys] = useState<Set<string>>(() => new Set());
  const [crewBypassTarget, setCrewBypassTarget] = useState<ComplianceDocumentOwnerGroup | null>(null);
  const [manualInviteLink, setManualInviteLink] = useState<string | null>(null);
  const [scopedContractors, setScopedContractors] = useState<Contractor[]>([]);
  const [crewRoster, setCrewRoster] = useState<ContractorCrewRosterRow[]>([]);
  const crewRosterRequestSeq = useRef(0);
  const [selectedSessionDbLink, setSelectedSessionDbLink] = useState<{
    sessionId: string;
    organizationId: string | null;
    contractorId: string | null;
    parentEntityId: string | null;
  } | null>(null);

  const session = sessionId ? onboarding.sessions.find((s) => s.id === sessionId) ?? null : null;
  const selectedToken = tokenId ? onboarding.inviteTokens.find((t) => t.id === tokenId) ?? null : null;
  const sessionToken = session?.tokenId
    ? onboarding.inviteTokens.find((token) => token.id === session.tokenId) ?? null
    : null;
  const selectedSessionDbContractorId =
    selectedSessionDbLink?.sessionId === sessionId ? selectedSessionDbLink.contractorId : null;
  const selectedSessionDbParentId =
    selectedSessionDbLink?.sessionId === sessionId ? selectedSessionDbLink.parentEntityId : null;
  const selectedSessionDbOrganizationId =
    selectedSessionDbLink?.sessionId === sessionId ? selectedSessionDbLink.organizationId : null;
  const selectedContractorId =
    realContractorId(session?.contractorId) ??
    realContractorId(selectedSessionDbContractorId) ??
    realContractorId(session?.parentEntityId) ??
    realContractorId(selectedSessionDbParentId) ??
    realContractorId(selectedToken?.contractorId) ??
    realContractorId(sessionToken?.contractorId) ??
    realContractorId(selectedToken?.parentId) ??
    realContractorId(sessionToken?.parentId) ??
    null;
  useEffect(() => {
    if (!sessionId) {
      setSelectedSessionDbLink(null);
      return;
    }
    let cancelled = false;
    void supabase
      .from("onboarding_sessions")
      .select("id, organization_id, contractor_id, parent_entity_id")
      .eq("id", sessionId)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.warn("UnifiedReviewerPanel.sessionContractorLookup:", error);
          setSelectedSessionDbLink(null);
          return;
        }
        setSelectedSessionDbLink({
          sessionId,
          organizationId: String(data?.organization_id ?? "").trim() || null,
          contractorId: String(data?.contractor_id ?? "").trim() || null,
          parentEntityId: String(data?.parent_entity_id ?? "").trim() || null,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        console.warn("UnifiedReviewerPanel.sessionContractorLookup:", error);
        setSelectedSessionDbLink(null);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);
  useEffect(() => {
    if (!selectedContractorId) return;
    if (contractors.some((c) => c.id === selectedContractorId)) return;
    if (scopedContractors.some((c) => c.id === selectedContractorId)) return;
    let cancelled = false;
    void ContractorService.fetchContractorById(selectedContractorId)
      .then((next) => {
        if (cancelled || !next) return;
        setScopedContractors((prev) => (prev.some((c) => c.id === next.id) ? prev : [next, ...prev]));
      })
      .catch(console.warn);
    return () => {
      cancelled = true;
    };
  }, [selectedContractorId, contractors, scopedContractors]);
  const contractorPool = useMemo(
    () => Array.from(
      new Map(
        [...contractors, ...scopedContractors].map((candidate) => [
          candidate.id,
          candidate,
        ]),
      ).values(),
    ),
    [contractors, scopedContractors],
  );
  useEffect(() => {
    if (!canonicalInternalReviewEnabled || !sessionId) return;
    let cancelled = false;
    void ContractorService.fetchContractors()
      .then((rows) => {
        if (cancelled) return;
        setScopedContractors((previous) => {
          const merged = new Map(
            [...previous, ...rows].map((candidate) => [
              candidate.id,
              candidate,
            ]),
          );
          return Array.from(merged.values());
        });
      })
      .catch(console.warn);
    return () => {
      cancelled = true;
    };
  }, [canonicalInternalReviewEnabled, sessionId]);
  const contractor = useMemo(
    () => {
      return selectedContractorId ? contractorPool.find((c) => c.id === selectedContractorId) ?? null : null;
    },
    [selectedContractorId, contractorPool],
  );
  const missingCanonicalAncestorId = useMemo(() => {
    if (!contractor) return null;
    const byId = new Map(contractorPool.map((candidate) => [
      candidate.id,
      candidate,
    ]));
    const visited = new Set<string>();
    let current = contractor;
    while (current.parentEntityId && !visited.has(current.id)) {
      visited.add(current.id);
      const parent = byId.get(current.parentEntityId);
      if (!parent) return realContractorId(current.parentEntityId);
      current = parent;
    }
    return null;
  }, [contractor, contractorPool]);
  useEffect(() => {
    if (!missingCanonicalAncestorId) return;
    let cancelled = false;
    void ContractorService.fetchContractorById(missingCanonicalAncestorId)
      .then((next) => {
        if (cancelled || !next) return;
        setScopedContractors((previous) =>
          previous.some((candidate) => candidate.id === next.id)
            ? previous
            : [next, ...previous]
        );
      })
      .catch(console.warn);
    return () => {
      cancelled = true;
    };
  }, [missingCanonicalAncestorId]);
  const canonicalParentContractor = useMemo(() => {
    return resolveCanonicalOnboardingParent(
      contractor?.id ?? selectedContractorId,
      contractorPool,
    );
  }, [contractor?.id, contractorPool, selectedContractorId]);
  const canonicalParentFamily = useMemo(() => {
    if (!canonicalParentContractor) return [];
    const included = new Set<string>([canonicalParentContractor.id]);
    let changed = true;
    while (changed) {
      changed = false;
      for (const candidate of contractorPool) {
        if (
          candidate.parentEntityId
          && included.has(candidate.parentEntityId)
          && !included.has(candidate.id)
        ) {
          included.add(candidate.id);
          changed = true;
        }
      }
    }
    return contractorPool.filter((candidate) =>
      included.has(candidate.id)
      && (
        !canonicalParentContractor.organizationId
        || candidate.organizationId
          === canonicalParentContractor.organizationId
      )
    );
  }, [canonicalParentContractor, contractorPool]);
  const crewLookupContractorIds = useMemo(() => {
    const ids = new Set<string>();
    const addId = (value: string | null | undefined) => {
      const id = realContractorId(value);
      if (id) ids.add(id);
    };

    addId(session?.contractorId);
    addId(selectedSessionDbContractorId);
    addId(session?.parentEntityId);
    addId(selectedSessionDbParentId);
    addId(selectedToken?.contractorId);
    addId(sessionToken?.contractorId);
    addId(selectedToken?.parentId);
    addId(sessionToken?.parentId);
    addId(contractor?.id);
    addId(canonicalParentContractor?.id);
    canonicalParentFamily.forEach((candidate) => addId(candidate.id));

    return Array.from(ids);
  }, [
    contractor?.id,
    canonicalParentContractor?.id,
    canonicalParentFamily,
    selectedToken?.contractorId,
    selectedToken?.parentId,
    sessionToken?.contractorId,
    sessionToken?.parentId,
    selectedSessionDbContractorId,
    selectedSessionDbParentId,
    session?.contractorId,
    session?.parentEntityId,
  ]);
  const canonicalRelatedPeople = useMemo(() => {
    if (!canonicalParentContractor) return [];
    const childContractorIds = new Set(
      canonicalParentFamily
        .filter((candidate) => candidate.id !== canonicalParentContractor.id)
        .map((candidate) => candidate.id),
    );
    const tokenById = new Map(
      onboarding.inviteTokens.map((token) => [token.id, token]),
    );
    const related: CanonicalOnboardingRelatedPerson[] = [];
    for (const candidate of onboarding.sessions) {
      const childContractorId = realContractorId(candidate.contractorId);
      if (!childContractorId || !childContractorIds.has(childContractorId)) {
        continue;
      }
      const token = tokenById.get(candidate.tokenId);
      const userId = compact(token?.consumedBy) || null;
      const childContractor = canonicalParentFamily.find(
        (entry) => entry.id === childContractorId,
      );
      related.push({
        contractorId: childContractorId,
        userId,
        label:
          compact(candidate.draft.contactName)
          || compact(candidate.draft.name)
          || childContractor?.companyName
          || "Related contractor person",
        relationshipLabel:
          childContractor?.contractorType === "sub_parent"
            ? "Sub-parent contractor"
            : childContractor?.relationshipType ?? candidate.entityType,
      });
    }
    return related;
  }, [
    canonicalParentContractor,
    canonicalParentFamily,
    onboarding.inviteTokens,
    onboarding.sessions,
  ]);
  const canonicalReviewContext = useMemo(
    () => buildCanonicalOnboardingReviewContext({
      organizationId:
        session?.organizationId
        ?? selectedSessionDbOrganizationId
        ?? canonicalParentContractor?.organizationId
        ?? contractor?.organizationId
        ?? null,
      selectedContractorId:
        contractor?.id
        ?? selectedContractorId,
      contractors: contractorPool,
      roster: crewRoster.map((person) => ({
        contractorId: person.contractorId,
        userId: person.userId,
        role: person.crewRole,
        status: person.status,
        label:
          person.fullName
          || person.email
          || (person.userId
            ? `Crew ${person.userId.slice(0, 8)}`
            : "Unlinked crew member"),
        email: person.email,
      })),
      relatedPeople: canonicalRelatedPeople,
    }),
    [
      canonicalParentContractor?.organizationId,
      canonicalRelatedPeople,
      contractor?.id,
      contractor?.organizationId,
      contractorPool,
      crewRoster,
      selectedContractorId,
      selectedSessionDbOrganizationId,
      session?.organizationId,
    ],
  );
  const canonicalPolicyOrganizationId = useMemo(() => {
    const trustedCandidates = [
      session?.organizationId,
      selectedSessionDbOrganizationId,
      canonicalParentContractor?.organizationId,
      contractor?.organizationId,
    ];

    return trustedCandidates
      .map((value) => compact(value))
      .find((value) => isUuidLike(value)) ?? null;
  }, [
    canonicalParentContractor?.organizationId,
    contractor?.organizationId,
    selectedSessionDbOrganizationId,
    session?.organizationId,
  ]);
  useEffect(() => {
    if (
      !canonicalInternalReviewEnabled
      && (
        section === "canonical_compliance"
        || section === "company_compliance"
        || section === "relationship_review"
      )
    ) {
      setSection("applicant");
    }
  }, [canonicalInternalReviewEnabled, section]);
  const fallbackCrewLookupContractorIds = useMemo(() => {
    if (crewLookupContractorIds.length > 0) return [];

    const ids = new Set<string>();
    const addId = (value: string | null | undefined) => {
      const id = realContractorId(value);
      if (id) ids.add(id);
    };
    const normalizedCompanyName = compact(contractor?.companyName ?? session?.draft?.name ?? "").toLowerCase();
    if (normalizedCompanyName) {
      contractorPool.forEach((row) => {
        const candidateCompany = compact(row.companyName).toLowerCase();
        const candidateContact = compact(row.contactName).toLowerCase();
        if (candidateCompany === normalizedCompanyName || candidateContact === normalizedCompanyName) {
          addId(row.id);
          addId(row.parentEntityId);
        }
      });
    }

    return Array.from(ids);
  }, [
    contractor?.companyName,
    crewLookupContractorIds,
    contractorPool,
    session?.draft?.name,
  ]);
  const crewLookupContractorNames = useMemo(
    () =>
      Array.from(
        new Set(
          [
            contractor?.companyName,
            contractor?.contactName,
            session?.draft?.name,
            session?.draft?.contactName,
          ].map(compact).filter(Boolean),
        ),
      ),
    [
      contractor?.companyName,
      contractor?.contactName,
      session?.draft?.contactName,
      session?.draft?.name,
    ],
  );
  const docs = useMemo(
    () => (session ? getReviewerDocuments(onboarding.complianceDocuments, session, contractor) : []),
    [session, contractor, onboarding.complianceDocuments],
  );
  const reviewerDocumentContractorIds = useMemo(
    () =>
      Array.from(
        new Set(
          docs
            .flatMap((doc) => [
              doc.contractorId,
              metadataString(doc, "contractorId"),
              metadataString(doc, "contractor_id"),
            ])
            .map(realContractorId)
            .filter((value): value is string => Boolean(value)),
        ),
      ),
    [docs],
  );
  const documentDerivedCrewRoster = useMemo(
    () => buildCrewRosterRowsFromDocuments(docs, contractor),
    [docs, contractor],
  );
  const mergedCrewRoster = useMemo(
    () => {
      const rows = mergeCrewRosterRows(
        [...crewRoster, ...documentDerivedCrewRoster],
        [
          ...draftCrewRows(session, contractor, onboarding.sessions),
          ...inviteTokenCrewRows(session, contractor, onboarding.inviteTokens, onboarding.sessions),
        ],
      );
      const scopedContractorIds = new Set(crewLookupContractorIds);
      if (scopedContractorIds.size === 0) return rows;
      return rows.filter((row) => {
        const rowContractorId = realContractorId(row.contractorId);
        return row.id.startsWith("draft-") || Boolean(rowContractorId && scopedContractorIds.has(rowContractorId));
      });
    },
    [
      contractor,
      crewLookupContractorIds,
      crewRoster,
      documentDerivedCrewRoster,
      onboarding.inviteTokens,
      onboarding.sessions,
      session,
    ],
  );
  const docGroups = useMemo(
    () => mergeCrewRosterIntoDocumentGroups(groupComplianceDocumentsByOwner(docs, contractor), mergedCrewRoster, contractor),
    [docs, contractor, mergedCrewRoster],
  );
  const docSummary = useMemo(
    () => (session ? summarizeDocuments(docs) : null),
    [session, docs],
  );
  const requirementSettings = useMemo(
    () =>
      session
        ? getOnboardingComplianceRequirementSettings(session, onboarding.sessions)
        : undefined,
    [session, onboarding.sessions],
  );
  const installerRequirements = useMemo(
    () =>
      session
        ? getRequiredOnboardingCompliance(session, onboarding.sessions)
        : null,
    [session, onboarding.sessions],
  );
  const crewRequirements = useMemo(() => {
    if (!session) return null;
    const previewDraft: ContractorOnboardingDraft = {
      ...session.draft,
      complianceRequirementSettings: requirementSettings,
    };
    return getRequiredOnboardingCompliance(
      {
        entityType: "crew",
        parentEntityId:
          realContractorId(session.contractorId) ?? session.parentEntityId,
        draft: previewDraft,
      },
      onboarding.sessions,
    );
  }, [session, requirementSettings, onboarding.sessions]);
  const installerRuleSet = useMemo(
    () => buildRequirementRuleSet(installerRequirements),
    [installerRequirements],
  );
  const crewRuleSet = useMemo(
    () => buildRequirementRuleSet(crewRequirements),
    [crewRequirements],
  );
  const requiredAgreement = useMemo(
    () => installerRequirements?.agreementVariant ?? null,
    [installerRequirements],
  );
  const agreementSignatures = useMemo(
    () => (session ? getSessionAgreementSignatures(session) : []),
    [session],
  );
  const requiredAgreementSignature = useMemo(
    () => agreementSignatures.find((sig) => sig.variant === requiredAgreement) ?? null,
    [agreementSignatures, requiredAgreement],
  );
  const signedAgreementDocument = useMemo(
    () => getSignedAgreementDocument(docs, requiredAgreement, requiredAgreementSignature),
    [docs, requiredAgreement, requiredAgreementSignature],
  );
  const readiness = useMemo(
    () => (session ? onboarding.getAdminApprovalReadiness(session.id) : null),
    [session, onboarding],
  );
  const complianceStatus = useMemo(() => {
    if (!contractor) return null;
    const parent = contractor.parentEntityId
      ? contractorPool.find((c) => c.id === contractor.parentEntityId)
      : undefined;
    try {
      return getComplianceStatus(contractor, onboarding.complianceDocuments, parent);
    } catch {
      return null;
    }
  }, [contractor, contractorPool, onboarding.complianceDocuments]);
  const stepDescriptors = useMemo(() => (session ? getOnboardingSteps(session) : []), [session]);

  const canDecide = ALLOWED_DECISION_ROLES.has(role);
  const canEditRequirementSettings =
    Boolean(session) && canDecide && !isCrewScopedEntity(session.entityType);
  const isTerminal = session?.status === "approved" || session?.status === "rejected";
  const actor = `${role}-user`;
  const draft = session?.draft ?? {};
  const intake = draft as typeof draft & {
    yearsInBusiness?: number | string;
    licenseNumber?: string;
    insuranceCarrier?: string;
    referralSource?: string;
    notes?: string;
  };

  useEffect(() => {
    let cancelled = false;
    const requestSeq = ++crewRosterRequestSeq.current;
    const isCurrentRequest = () => !cancelled && requestSeq === crewRosterRequestSeq.current;
    void (async () => {
      try {
        const nameResolvedIds = crewLookupContractorIds.length > 0
          ? []
          : await resolveContractorIdsByName(crewLookupContractorNames);
        const ids = crewLookupContractorIds.length > 0
          ? crewLookupContractorIds
          : Array.from(new Set([...fallbackCrewLookupContractorIds, ...reviewerDocumentContractorIds, ...nameResolvedIds]));
        if (ids.length === 0) {
          if (isCurrentRequest()) setCrewRoster([]);
          return;
        }
        const rows = await loadContractorCrewRoster(ids);
        if (isCurrentRequest()) setCrewRoster(rows);
      } catch (error) {
        console.warn("UnifiedReviewerPanel.loadContractorCrewRoster:", error);
        if (isCurrentRequest()) setCrewRoster([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    crewLookupContractorIds,
    crewLookupContractorNames,
    fallbackCrewLookupContractorIds,
    reviewerDocumentContractorIds,
    sessionId,
  ]);

  function close() {
    const next = new URLSearchParams(params);
    next.delete("session");
    next.delete("token");
    next.set("noAuto", "1");
    setParams(next, { replace: true });
  }

  async function handleVerify(documentId: string) {
    try {
      const doc = onboarding.complianceDocuments.find((d) => d.id === documentId);
      if (doc && isWebsiteComplianceDocument(doc)) await approveInstallerComplianceDocument(documentId);
      onboarding.verifyComplianceDocument(documentId, actor);
      toast({ title: "Document verified", description: "Compliance status refreshed." });
    } catch (e) {
      toast({ title: "Couldn't verify document", description: (e as Error).message, variant: "destructive" });
    }
  }

  async function handleRejectConfirm() {
    if (!rejectTarget) return;
    if (rejectReason.trim().length < 5) {
      toast({ title: "Reason too short", description: "Please provide a brief rejection reason.", variant: "destructive" });
      return;
    }
    try {
      const doc = onboarding.complianceDocuments.find((d) => d.id === rejectTarget.documentId);
      if (doc && isWebsiteComplianceDocument(doc)) {
        await rejectInstallerComplianceDocument(rejectTarget.documentId, rejectReason.trim());
      }
      onboarding.rejectComplianceDocument(rejectTarget.documentId, actor, rejectReason.trim());
      setRejectTarget(null);
      setRejectReason("");
      toast({ title: "Document rejected" });
    } catch (e) {
      toast({ title: "Couldn't reject document", description: (e as Error).message, variant: "destructive" });
    }
  }

  async function handleViewDocument(doc: ComplianceDocument) {
    try {
      const storagePath = getComplianceDocumentStoragePath(doc);
      if (!storagePath) throw new Error("This document is missing its storage path.");
      const href = isWebsiteComplianceDocument(doc)
        ? await getInstallerComplianceDocumentReadUrl(doc.id, 300)
        : storagePath;
      if (!href) throw new Error("Document URL was not returned.");
      setDocumentPreview({ doc, href });
    } catch (e) {
      toast({ title: "Couldn't open document", description: (e as Error).message, variant: "destructive" });
    }
  }

  function handleRequirementSettingChange(
    audience: "installer" | "crew",
    key: keyof OnboardingComplianceRequirementRuleSet,
    checked: boolean,
  ) {
    if (!session || !canEditRequirementSettings) return;
    const installerBase = buildCompleteRequirementRuleSet(
      requirementSettings?.installer ?? installerRuleSet,
    );
    const crewBase = buildCompleteRequirementRuleSet(
      requirementSettings?.crew ?? crewRuleSet,
    );
    const nextSettings = {
      installer:
        audience === "installer"
          ? { ...installerBase, [key]: checked }
          : installerBase,
      crew:
        audience === "crew"
          ? { ...crewBase, [key]: checked }
          : crewBase,
      updatedAt: new Date().toISOString(),
      updatedBy: actor,
    };
    onboarding.updateOnboardingDraft(
      session.id,
      {
        complianceRequirementSettings: nextSettings,
      } as Partial<ContractorOnboardingDraft>,
      actor,
    );
  }

  async function handleActivateCrew(group: ComplianceDocumentOwnerGroup) {
    try {
      const contractorId = compact(group.contractorId ?? group.docs[0]?.contractorId);
      const organizationId = compact(contractor?.organizationId ?? group.docs[0]?.metadata?.organizationId);
      if (!contractorId || !organizationId) throw new Error("Crew group is missing contractor/org linkage.");
      if (!canActivateCrewGroup(group, activatedCrewKeys)) {
        throw new Error("Verify all crew documents before activating this crew member.");
      }
      const identifiers = resolveCrewActivationIdentifiers(group);
      if (!identifiers.contractorCrewId && !identifiers.crewUserId) {
        throw new Error("Crew group is missing a valid activation identifier.");
      }
      await activateInstallerComplianceCrew({
        organizationId,
        contractorId,
        contractorCrewId: identifiers.contractorCrewId,
        crewUserId: identifiers.crewUserId,
      });
      setActivatedCrewKeys((prev) => new Set(prev).add(getCrewActivationKey(group)));
      toast({ title: "Crew activated" });
    } catch (e) {
      toast({ title: "Couldn't activate crew", description: (e as Error).message, variant: "destructive" });
    }
  }

  async function handleBypassActivateCrew(group: ComplianceDocumentOwnerGroup) {
    try {
      const contractorId = compact(group.contractorId ?? group.docs[0]?.contractorId);
      const organizationId = compact(contractor?.organizationId ?? group.docs[0]?.metadata?.organizationId);
      if (!contractorId || !organizationId) throw new Error("Crew group is missing contractor/org linkage.");
      if (!canBypassCrewActivationGroup(group, activatedCrewKeys)) {
        throw new Error("This crew cannot be bypass-activated.");
      }
      const identifiers = resolveCrewActivationIdentifiers(group);
      if (!identifiers.contractorCrewId && !identifiers.crewUserId) {
        throw new Error("Crew group is missing a valid activation identifier.");
      }
      await activateInstallerComplianceCrew({
        organizationId,
        contractorId,
        contractorCrewId: identifiers.contractorCrewId,
        crewUserId: identifiers.crewUserId,
      });
      setActivatedCrewKeys((prev) => new Set(prev).add(getCrewActivationKey(group)));
      setCrewBypassTarget(null);
      toast({
        title: "Crew activated via bypass",
        description: "Reviewer override was used to activate this crew before all compliance checks were completed.",
      });
    } catch (e) {
      toast({ title: "Couldn't bypass crew activation", description: (e as Error).message, variant: "destructive" });
    }
  }

  async function handleSendInvite() {
    if (!session) return;
    try {
      const identity = resolveInviteIdentity(session);
      if (!identity?.email) throw new Error("Applicant email is missing on this application.");
      const organizationId = compact(contractor?.organizationId);
      if (!session.contractorId || !organizationId) {
        throw new Error("Application is missing contractor/org linkage.");
      }
      const { data, error } = await supabase.functions.invoke<{
        delivery?: string;
        actionLink?: string | null;
      }>("create-auth-user", {
        body: {
          email: identity.email,
          fullName: identity.fullName,
          organizationId,
          contractorId: session.contractorId,
          orgRole: "owner",
          sendInvite: true,
          redirectTo: getInstallerPortalInviteRedirect(),
        },
      });
      if (error) throw error;
      if (data?.delivery === "manual_link" && data.actionLink) {
        await navigator.clipboard?.writeText(data.actionLink).catch(() => undefined);
        setManualInviteLink(data.actionLink);
        toast({
          title: "Invite link generated",
          description: "Supabase could not send the email, so a manual portal invite link is ready.",
        });
        return;
      }
      toast({ title: "Invite sent", description: `Portal invite sent to ${identity.email}.` });
    } catch (e) {
      toast({ title: "Couldn't send invite", description: (e as Error).message, variant: "destructive" });
    }
  }

  function handleVerifyPayout() {
    if (!session) return;
    try {
      if (!session.payoutMethod) throw new Error("No payout method is recorded for this application.");
      onboarding.setOnboardingPayout(
        session.id,
        {
          type: session.payoutMethod.type,
          last4: session.payoutMethod.last4,
          parentEntityId: session.payoutMethod.parentEntityId,
          placeholderVerified: true,
        },
        actor,
      );
      toast({ title: "Payout verified", description: "Payout verification warning cleared." });
    } catch (e) {
      toast({ title: "Couldn't verify payout", description: (e as Error).message, variant: "destructive" });
    }
  }

  async function handleApprove() {
    if (!session || confirmText.trim() !== APPROVE_CONFIRM || reason.trim().length < MIN_REASON) return;
    try {
      const result = onboarding.approveOnboarding(session.id, actor, reason.trim());
      if (result.contractorPatch) {
        const { contractorId, vettingStatus } = result.contractorPatch;
        const contractorUpdates: Partial<Contractor> = {
          active: true,
          availabilityStatus: "Available",
          marketplaceOptIn: true,
          ...(getSessionServiceAreaLabel(session, contractor) ? { serviceArea: getSessionServiceAreaLabel(session, contractor) } : {}),
          ...(getSessionServiceRegions(session, contractor).length > 0 ? { serviceRegions: getSessionServiceRegions(session, contractor) } : {}),
          ...(getSessionServiceZipCodes(session, contractor).length > 0 ? { serviceZipCodes: getSessionServiceZipCodes(session, contractor) } : {}),
          ...(getSessionMaxWeeklyJobs(session, contractor) ? { maxWeeklyJobs: getSessionMaxWeeklyJobs(session, contractor)! } : {}),
          ...(getSessionTradeLabel(session, contractor) ? { tradeType: getSessionTradeLabel(session, contractor) } : {}),
        };
        const persistedContractor = await ContractorService.updateContractor(contractorId, contractorUpdates);
        setContractors((prev) =>
          prev.map((c) =>
            c.id === contractorId
              ? {
                  ...c,
                  ...(persistedContractor ?? contractorUpdates),
                  vettingStatus,
                  onboardingStatus: "Approved",
                }
              : c,
          ),
        );
        const onboardingPayProfile = buildPayProfileFromOnboarding(contractorId, session, contractor);
        if (onboardingPayProfile) {
          setPayProfiles((prev) => mergePayProfile(prev, onboardingPayProfile, contractorId));
        }
      }
      setApproveOpen(false);
      setReason("");
      setConfirmText("");
      toast({ title: "Onboarding approved" });
    } catch (e) {
      toast({ title: "Couldn't approve", description: (e as Error).message, variant: "destructive" });
    }
  }

  function handleRejectSession() {
    if (!session || reason.trim().length < MIN_REASON) return;
    try {
      onboarding.rejectOnboarding(session.id, actor, reason.trim());
      setRejectOpen(false);
      setReason("");
      toast({ title: "Onboarding rejected" });
    } catch (e) {
      toast({ title: "Couldn't reject", description: (e as Error).message, variant: "destructive" });
    }
  }

  function handleReupload() {
    if (!session || reason.trim().length < MIN_REASON || reuploadSteps.length === 0) return;
    try {
      onboarding.requestOnboardingReupload(session.id, reuploadSteps, reason.trim(), actor);
      setReuploadOpen(false);
      setReason("");
      setReuploadSteps([]);
      toast({ title: "Reupload requested" });
    } catch (e) {
      toast({ title: "Couldn't request reupload", description: (e as Error).message, variant: "destructive" });
    }
  }

  if (!session) {
    return selectedToken ? (
      <InviteReviewer
        token={selectedToken}
        contractor={contractor}
        documents={onboarding.complianceDocuments}
        canDecide={canDecide}
        onClose={close}
        onViewDocument={handleViewDocument}
        onVerifyDocument={handleVerify}
        onRejectDocument={(doc) => setRejectTarget({ contractorId: doc.contractorId, documentId: doc.id })}
        rejectTarget={rejectTarget}
        rejectReason={rejectReason}
        setRejectReason={setRejectReason}
        clearRejectTarget={() => setRejectTarget(null)}
        handleRejectConfirm={handleRejectConfirm}
        documentPreview={documentPreview}
        closeDocumentPreview={() => setDocumentPreview(null)}
      />
    ) : (
      <IdleReviewer />
    );
  }

  return (
    <Card className="sticky top-4" data-testid="unified-reviewer-panel">
      <CardContent className="p-0">
        <div className="flex items-start justify-between gap-3 p-4 border-b">
          <div className="min-w-0">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Reviewing</div>
            <div className="text-base font-semibold truncate">
              {draft.name?.trim() || contractor?.companyName || `Session ${session.id.slice(-6)}`}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {session.entityType} / {getCanonicalSessionStatusLabel(session, "admin")}
            </div>
          </div>
          <Button size="icon" variant="ghost" onClick={close} aria-label="Close reviewer">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex flex-wrap gap-1 p-2 border-b bg-muted/20">
          {SECTIONS.filter(
            (candidate) =>
              !candidate.canonicalOnly || canonicalInternalReviewEnabled,
          ).map((s) => {
            const Icon = s.icon;
            const active = section === s.id;
            return (
              <button
                key={s.id}
                onClick={() => setSection(s.id)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] transition-colors ${
                  active ? "bg-primary/15 text-primary ring-1 ring-primary/30" : "text-muted-foreground hover:bg-muted"
                }`}
                aria-pressed={active}
              >
                <Icon className="h-3 w-3" />
                {s.label}
              </button>
            );
          })}
        </div>

        <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
          {section === "applicant" && (
            <div className="space-y-2 text-sm">
              <Row label="Name" value={draft.name ?? contractor?.companyName} />
              <Row label="Contact" value={draft.contactName ?? contractor?.contactName} />
              <Row label="Email" value={draft.contactEmail ?? contractor?.email} />
              <Row label="Phone" value={draft.contactPhone ?? contractor?.phone} />
              <Row label="Entity" value={session.entityType} />
              <Row label="Trade" value={getSessionTradeLabel(session, contractor)} />
              <Row label="Service regions" value={getSessionServiceRegions(session, contractor).join(", ")} />
              <Row label="Service ZIPs" value={getSessionServiceZipCodes(session, contractor).join(", ")} />
              <div className="pt-2 mt-2 border-t">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Intake details</div>
                <Row label="Years in business" value={intake.yearsInBusiness ? String(intake.yearsInBusiness) : undefined} />
                <Row label="License #" value={intake.licenseNumber} />
                <Row label="Insurance carrier" value={intake.insuranceCarrier} />
                <Row label="Referral source" value={intake.referralSource} />
                <Row label="Notes" value={intake.notes} multiline />
              </div>
            </div>
          )}

          {section === "crews" && (
            <div className="space-y-3">
              {mergedCrewRoster.length === 0 ? (
                <p className="text-xs text-muted-foreground">No crews linked to this contractor yet.</p>
              ) : (
                mergedCrewRoster.map((crew) => {
                  const matchingGroup = docGroups.find((group) => crewGroupMatchesRosterRow(group, crew));
                  const ownerLabel =
                    crew.fullName ||
                    crew.email ||
                    (crew.userId ? `Crew ${crew.userId.slice(0, 8)}` : `Crew ${crew.id.slice(0, 8)}`);
                  const isDraftOnlyCrew = crew.id.startsWith("draft-");
                  const isTokenOnlyCrew = crew.id.startsWith("draft-token:");
                  const sourceLabel = isTokenOnlyCrew
                    ? "Pending invite token"
                    : isDraftOnlyCrew
                      ? "Onboarding draft invitee"
                      : "Live contractor crew";

                  return (
                    <div key={crew.id} className="rounded-md border p-3 space-y-3">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold">{ownerLabel}</p>
                          <p className="truncate text-[11px] text-muted-foreground">
                            {crew.email || "No email on file"}
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline" className={isDraftOnlyCrew ? "text-muted-foreground" : crewStatusTone(crew.status)}>
                            {isDraftOnlyCrew ? "Draft only" : crewStatusLabel(crew.status)}
                          </Badge>
                          {isDraftOnlyCrew && crew.status ? (
                            <Badge variant="outline" className={crewStatusTone(crew.status)}>
                              Invite: {crewStatusLabel(crew.status)}
                            </Badge>
                          ) : null}
                          {crew.crewRole ? (
                            <Badge variant="secondary" className="capitalize">
                              {crew.crewRole.replace(/_/g, " ")}
                            </Badge>
                          ) : null}
                        </div>
                      </div>

                      <div className="grid gap-2 sm:grid-cols-2">
                        <Row label="Crew id" value={crew.id} />
                        <Row label="Installer user" value={crew.userId} />
                        <Row label="Source" value={sourceLabel} />
                        <Row label={isDraftOnlyCrew ? "Draft invite status" : "Live crew status"} value={crewStatusLabel(crew.status)} />
                        <Row label="Role" value={crew.crewRole?.replace(/_/g, " ")} />
                        <Row label="Compliance docs" value={matchingGroup ? String(matchingGroup.docs.length) : "0"} />
                      </div>

                      {matchingGroup && !isCrewGroupActive(matchingGroup, activatedCrewKeys) && canDecide ? (
                        <div className="flex flex-wrap gap-2 pt-1">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!canActivateCrewGroup(matchingGroup, activatedCrewKeys)}
                            onClick={() => void handleActivateCrew(matchingGroup)}
                          >
                            Activate crew
                          </Button>
                          {canBypassCrewActivationGroup(matchingGroup, activatedCrewKeys) ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-warning/40 text-warning hover:bg-warning/10"
                              onClick={() => setCrewBypassTarget(matchingGroup)}
                            >
                              Bypass activation
                            </Button>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  );
                })
              )}
            </div>
          )}

          {section === "docs" && (
            <div className="space-y-3">
              {docSummary && (
                <div className="flex items-center gap-2 text-[11px]">
                  <Badge variant="outline" className="text-success">{docSummary.verified} verified</Badge>
                  <Badge variant="outline" className="text-warning">{docSummary.pending} pending</Badge>
                  {docSummary.rejected > 0 && <Badge variant="outline" className="text-destructive">{docSummary.rejected} rejected</Badge>}
                </div>
              )}
              {docGroups.length === 0 ? (
                <p className="text-xs text-muted-foreground">No documents on file.</p>
              ) : (
                docGroups.map((group) => (
                  <div key={group.key} className="space-y-1.5 rounded-md border p-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold">{group.label}</p>
                        <p className="truncate text-[11px] text-muted-foreground">{group.subtitle}</p>
                      </div>
                      {group.ownerKind === "crew" && !isCrewGroupActive(group, activatedCrewKeys) && (
                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" variant="outline" disabled={!canActivateCrewGroup(group, activatedCrewKeys)} onClick={() => void handleActivateCrew(group)}>
                            Activate crew
                          </Button>
                          {canDecide && canBypassCrewActivationGroup(group, activatedCrewKeys) ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-warning/40 text-warning hover:bg-warning/10"
                              onClick={() => setCrewBypassTarget(group)}
                            >
                              Bypass activation
                            </Button>
                          ) : null}
                        </div>
                      )}
                    </div>
                    {group.docs.length === 0 ? (
                      <div className="rounded border border-dashed px-2 py-2 text-xs text-muted-foreground">
                        No compliance documents uploaded for this crew yet.
                      </div>
                    ) : (
                      group.docs.map((d) => (
                        <div key={d.id} className="flex items-center justify-between gap-2 border rounded px-2 py-1.5 text-xs">
                          <div className="min-w-0">
                            <div className="font-mono truncate">{d.type}</div>
                            <div className="text-muted-foreground truncate">
                              {d.verifiedAt ? `verified ${new Date(d.verifiedAt).toLocaleDateString()}` : d.rejectedAt ? `rejected: ${d.rejectionReason}` : "pending verification"}
                              {d.expiresAt ? ` / exp ${new Date(d.expiresAt).toLocaleDateString()}` : ""}
                            </div>
                          </div>
                          <div className="flex gap-1 shrink-0">
                            {getComplianceDocumentStoragePath(d) && (
                              <Button size="sm" variant="outline" onClick={() => void handleViewDocument(d)}>View</Button>
                            )}
                            {!d.verifiedAt && !d.rejectedAt && canDecide && (
                              <>
                                <Button size="sm" variant="outline" onClick={() => void handleVerify(d.id)}>Verify</Button>
                                <Button size="sm" variant="outline" onClick={() => setRejectTarget({ contractorId: d.contractorId, documentId: d.id })}>Reject</Button>
                              </>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {section === "canonical_compliance" && canonicalInternalReviewEnabled && (
            canonicalReviewContext ? (
              <CanonicalIndividualComplianceReviewPanel
                contractors={canonicalParentFamily}
                onboardingContext={canonicalReviewContext}
                contextKey={[
                  session.id,
                  canonicalReviewContext.organizationId,
                  canonicalReviewContext.parentContractorId,
                ].join(":")}
                scopeLabel={`Reviews are selected under parent ${canonicalReviewContext.parentContractorName}; reviewer authority remains server-derived.`}
              />
            ) : (
              <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
                A trusted organization, parent contractor, and active person
                relationship are required before canonical Individual
                Compliance review can be loaded.
              </div>
            )
          )}

          { console.log(section,canonicalInternalReviewEnabled,canonicalReviewContext)}
          {section === "company_compliance" && canonicalInternalReviewEnabled && (
            canonicalReviewContext ? (
              <CanonicalCompanyComplianceReadPanel
                organizationId={canonicalReviewContext.organizationId}
                contractorId={canonicalReviewContext.parentContractorId}
                contractorLabel={canonicalReviewContext.parentContractorName}
                contextKey={[
                  session.id,
                  canonicalReviewContext.organizationId,
                  canonicalReviewContext.parentContractorId,
                  "company",
                ].join(":")}
              />
            ) : (
              <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
                A trusted organization and parent contractor are required
                before canonical Company Compliance review can be loaded.
              </div>
            )
          )}

          {section === "relationship_review" && canonicalInternalReviewEnabled && (
            canonicalReviewContext ? (
              <CanonicalRelationshipReviewReadPanel
                organizationId={canonicalReviewContext.organizationId}
                parentContractorId={canonicalReviewContext.parentContractorId}
                parentContractorLabel={canonicalReviewContext.parentContractorName}
                contractors={canonicalParentFamily}
                contextKey={[
                  session.id,
                  canonicalReviewContext.organizationId,
                  canonicalReviewContext.parentContractorId,
                  "relationship",
                ].join(":")}
              />
            ) : (
              <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
                A trusted organization and parent contractor are required
                before Relationship Review can be loaded.
              </div>
            )
          )}

          {section === "requirements" && (
            <div className="space-y-4">
              {canonicalPolicyOrganizationId ? (
                <CanonicalCompanyCompliancePolicyEditor
                  organizationId={canonicalPolicyOrganizationId}
                />
              ) : (
                <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
                  Select a trusted organization context to manage Canonical Company Compliance policy.
                </div>
              )}

              {canonicalPolicyOrganizationId ? (
                <CanonicalIndividualCompliancePolicyEditor
                  organizationId={canonicalPolicyOrganizationId}
                />
              ) : (
                <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
                  Select a trusted organization context to manage Canonical Individual Compliance policy.
                </div>
              )}

              <div className="rounded-md border p-3 space-y-3">
                <div>
                  <div className="text-sm font-semibold">Relationship Review Requirements</div>
                  <p className="text-xs text-muted-foreground">
                    Relationship-scoped requirements remain read-only here until the dedicated Website policy runtime is implemented and validated.
                  </p>
                </div>
                <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
                  Policy management is not yet available in App/Ops. Relationship Review requirements require a separate versioned policy contract with parent-child authorization and rollout behavior.
                </div>
              </div>

              <div className="rounded-md border p-3 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold">Legacy onboarding upload settings</div>
                    <p className="text-xs text-muted-foreground">
                      These legacy settings do not control Canonical Company Compliance, Canonical Individual Compliance, or Relationship Review policies.
                    </p>
                  </div>
                  <Badge variant="outline">
                    Agreement: {installerRequirements ? AGREEMENT_LABELS[installerRequirements.agreementVariant] : "—"}
                  </Badge>
                </div>
                <div className="grid gap-2 md:grid-cols-2">
                  {REQUIREMENT_DOCUMENT_FIELDS.map((field) => {
                    const checkboxId = `installer-${String(field.key)}`;
                    return (
                      <label
                        key={checkboxId}
                        htmlFor={checkboxId}
                        className="flex items-center gap-2 rounded border px-2 py-2 text-xs"
                      >
                        <Checkbox
                          id={checkboxId}
                          checked={requirementRuleValue(installerRuleSet, field.key)}
                          disabled={!canEditRequirementSettings}
                          onCheckedChange={(value) =>
                            handleRequirementSettingChange(
                              "installer",
                              field.key,
                              value === true,
                            )
                          }
                        />
                        <span>{field.label}</span>
                      </label>
                    );
                  })}
                </div>
                <div className="grid gap-2 md:grid-cols-1">
                  {REQUIREMENT_INHERIT_FIELDS.map((field) => {
                    const checkboxId = `installer-${String(field.key)}`;
                    return (
                      <label
                        key={checkboxId}
                        htmlFor={checkboxId}
                        className="flex items-center gap-2 rounded border px-2 py-2 text-xs"
                      >
                        <Checkbox
                          id={checkboxId}
                          checked={requirementRuleValue(installerRuleSet, field.key)}
                          disabled={!canEditRequirementSettings}
                          onCheckedChange={(value) =>
                            handleRequirementSettingChange(
                              "installer",
                              field.key,
                              value === true,
                            )
                          }
                        />
                        <span>{field.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="rounded-md border p-3 space-y-3">
                <div>
                  <div className="text-sm font-semibold">Crew requirements</div>
                  <p className="text-xs text-muted-foreground">
                    Sets what invited crew members must upload before their onboarding is considered complete.
                  </p>
                </div>
                <div className="grid gap-2 md:grid-cols-2">
                  {REQUIREMENT_DOCUMENT_FIELDS.map((field) => {
                    const checkboxId = `crew-${String(field.key)}`;
                    return (
                      <label
                        key={checkboxId}
                        htmlFor={checkboxId}
                        className="flex items-center gap-2 rounded border px-2 py-2 text-xs"
                      >
                        <Checkbox
                          id={checkboxId}
                          checked={requirementRuleValue(crewRuleSet, field.key)}
                          disabled={!canEditRequirementSettings}
                          onCheckedChange={(value) =>
                            handleRequirementSettingChange(
                              "crew",
                              field.key,
                              value === true,
                            )
                          }
                        />
                        <span>{field.label}</span>
                      </label>
                    );
                  })}
                </div>
                <div className="grid gap-2 md:grid-cols-1">
                  {REQUIREMENT_INHERIT_FIELDS.map((field) => {
                    const checkboxId = `crew-${String(field.key)}`;
                    return (
                      <label
                        key={checkboxId}
                        htmlFor={checkboxId}
                        className="flex items-center gap-2 rounded border px-2 py-2 text-xs"
                      >
                        <Checkbox
                          id={checkboxId}
                          checked={requirementRuleValue(crewRuleSet, field.key)}
                          disabled={!canEditRequirementSettings}
                          onCheckedChange={(value) =>
                            handleRequirementSettingChange(
                              "crew",
                              field.key,
                              value === true,
                            )
                          }
                        />
                        <span>{field.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {!canEditRequirementSettings ? (
                <p className="text-xs text-muted-foreground">
                  Requirement settings are editable only by reviewer roles from the main contractor onboarding session.
                </p>
              ) : null}

              {complianceStatus?.requirements?.length ? (
                <div className="space-y-1 rounded-md border p-3">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Current company compliance state
                  </div>
                  {complianceStatus.requirements.map((r) => (
                    <div key={r.requirement} className="flex items-center justify-between gap-2 text-xs border rounded px-2 py-1">
                      <span className="font-mono">{r.requirement}</span>
                      <Badge variant="outline" className={requirementTone(r.status)}>
                        {r.status}{r.inheritedFromParent ? " / inherited" : ""}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          )}

          {section === "agreement" && (
            <div className="space-y-3 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Agreement signatures</div>
                <div className="rounded-md border px-2 py-2 text-xs space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium truncate">
                        {requiredAgreement ? AGREEMENT_LABELS[requiredAgreement] : "Agreement"}
                      </div>
                      <div className="text-muted-foreground">
                        Version {requiredAgreementSignature?.version ?? AGREEMENT_VERSION} / {requiredAgreement ?? "unknown"}
                      </div>
                      {requiredAgreementSignature ? (
                        <div className="text-muted-foreground">
                          Signed {new Date(requiredAgreementSignature.signedAt).toLocaleString()} by {requiredAgreementSignature.typedName ?? requiredAgreementSignature.signedBy}
                        </div>
                      ) : (
                        <div className="text-muted-foreground">No signature captured yet.</div>
                      )}
                      {requiredAgreementSignature?.ipAddress ? (
                        <div className="text-muted-foreground">IP {requiredAgreementSignature.ipAddress}</div>
                      ) : null}
                    </div>
                    <Badge variant="outline" className={requiredAgreementSignature ? "text-success" : "text-warning"}>
                      {requiredAgreementSignature ? "Signed" : "Pending signature"}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => signedAgreementDocument ? void handleViewDocument(signedAgreementDocument) : setAgreementPreviewOpen(true)}
                    >
                      {requiredAgreementSignature ? "View signed " : "View "}
                      {requiredAgreement === "1099_msa" ? "1099 MSA" : "Agreement"}
                    </Button>
                  </div>
                  {signedAgreementDocument ? (
                    <div className="truncate text-[11px] text-muted-foreground">
                      Signed PDF: {getComplianceDocumentFileName(signedAgreementDocument)}
                    </div>
                  ) : (
                    <div className="text-[11px] text-warning">
                      Signed PDF: {requiredAgreementSignature ? "Unavailable" : "Not submitted"}
                    </div>
                  )}
                  {!requiredAgreementSignature && (
                    <div className="text-[11px] text-muted-foreground">
                      Agreement signature status: Pending
                    </div>
                  )}
                  {agreementSignatures.some((sig) => sig.variant !== requiredAgreement) ? (
                    <div className="rounded border border-dashed bg-muted/20 p-2 text-muted-foreground">
                      Other captured variants: {agreementSignatures
                        .filter((sig) => sig.variant !== requiredAgreement)
                        .map((sig) => AGREEMENT_LABELS[sig.variant] ?? sig.variant)
                        .join(", ")}
                    </div>
                  ) : null}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Payout method</div>
                {!session.payoutMethod ? (
                  <p className="text-xs text-muted-foreground">Not provided.</p>
                ) : (
                  <div className="border rounded px-2 py-1.5 text-xs space-y-1">
                    <div className="font-medium">{session.payoutMethod.type}</div>
                    {session.payoutMethod.last4 && <div className="text-muted-foreground">last4 {session.payoutMethod.last4}</div>}
                    {session.payoutMethod.verified ? (
                      <div className="text-success">verified</div>
                    ) : canDecide ? (
                      <Button size="sm" variant="outline" onClick={handleVerifyPayout}>Verify payout</Button>
                    ) : (
                      <div className="text-warning">pending verification</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {section === "decision" && (
            <div className="space-y-3">
              <ReadinessSummary readiness={readiness} />
              {!canDecide ? (
                <p className="text-xs text-muted-foreground">Decisions are restricted to SuperAdmin / Admin / Ops Manager.</p>
              ) : isTerminal ? (
                <p className="text-xs text-muted-foreground">Session is terminal ({session.status}). No further decisions available.</p>
              ) : (
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button size="sm" variant="outline" onClick={() => void handleSendInvite()}>
                    <Send className="h-3.5 w-3.5 mr-1" /> Send invite
                  </Button>
                  <Button size="sm" onClick={() => { setApproveOpen(true); setReason(""); setConfirmText(""); }} disabled={Boolean(readiness && readiness.blockers.length > 0)}>
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setReuploadOpen(true); setReason(""); setReuploadSteps([]); }}>
                    <Clock className="h-3.5 w-3.5 mr-1" /> Request reupload
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setRejectOpen(true); setReason(""); }}>
                    <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>

      <DecisionDialogs
        approveOpen={approveOpen}
        setApproveOpen={setApproveOpen}
        rejectOpen={rejectOpen}
        setRejectOpen={setRejectOpen}
        reuploadOpen={reuploadOpen}
        setReuploadOpen={setReuploadOpen}
        reason={reason}
        setReason={setReason}
        confirmText={confirmText}
        setConfirmText={setConfirmText}
        reuploadSteps={reuploadSteps}
        setReuploadSteps={setReuploadSteps}
        stepDescriptors={stepDescriptors}
        session={session}
        handleApprove={handleApprove}
        handleRejectSession={handleRejectSession}
        handleReupload={handleReupload}
      />

      <RejectDocumentDialog
        open={!!rejectTarget}
        onOpenChange={(open) => !open && setRejectTarget(null)}
        rejectReason={rejectReason}
        setRejectReason={setRejectReason}
        onConfirm={handleRejectConfirm}
      />

      <DocumentPreviewDialog
        preview={documentPreview}
        onClose={() => setDocumentPreview(null)}
      />

      <AgreementPreviewDialog
        open={agreementPreviewOpen}
        onClose={() => setAgreementPreviewOpen(false)}
        session={session}
        contractor={contractor}
        variant={requiredAgreement}
        signature={requiredAgreementSignature}
      />

      <Dialog open={!!crewBypassTarget} onOpenChange={(open) => !open && setCrewBypassTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bypass crew activation checks</DialogTitle>
            <DialogDescription>
              This will activate the crew even if compliance documents are still pending or unverified.
            </DialogDescription>
          </DialogHeader>
          {crewBypassTarget ? (
            <div className="space-y-3 text-sm">
              <div className="rounded-md border border-warning/30 bg-warning/5 p-3 text-warning">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    Use this only when you intentionally want the crew active before document review is complete.
                  </div>
                </div>
              </div>
              <div className="space-y-1">
                <div className="font-medium">{crewBypassTarget.label}</div>
                <div className="text-muted-foreground">{crewBypassTarget.subtitle}</div>
                <div className="text-xs text-muted-foreground">
                  Verified docs: {crewBypassTarget.docs.filter((doc) => !!doc.verifiedAt && !doc.rejectedAt).length} / {crewBypassTarget.docs.length}
                </div>
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCrewBypassTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => crewBypassTarget && void handleBypassActivateCrew(crewBypassTarget)}
            >
              Activate with bypass
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!manualInviteLink} onOpenChange={(open) => !open && setManualInviteLink(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manual invite link</DialogTitle>
            <DialogDescription>
              Supabase could not send the invite email. Send this link to the installer so they can activate their portal access.
            </DialogDescription>
          </DialogHeader>
          <Input value={manualInviteLink ?? ""} readOnly className="font-mono text-xs" />
          <DialogFooter>
            <Button variant="outline" onClick={() => manualInviteLink && window.open(manualInviteLink, "_blank", "noopener,noreferrer")}>
              Open link
            </Button>
            <Button onClick={() => manualInviteLink && navigator.clipboard?.writeText(manualInviteLink)}>
              Copy link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function mergePayProfile(
  prev: ContractorPayProfile[],
  onboardingPayProfile: ContractorPayProfile,
  contractorId: string,
): ContractorPayProfile[] {
  const existing = prev.find((profile) => profile.contractorId === contractorId);
  if (!existing) return [...prev, onboardingPayProfile];
  const mergedSpecTypes = Array.from(new Set([...existing.allowedSpecTypes, ...onboardingPayProfile.allowedSpecTypes]));
  return prev.map((profile) =>
    profile.contractorId === contractorId
      ? { ...profile, isActive: true, allowedSpecTypes: mergedSpecTypes, updatedAt: new Date().toISOString() }
      : profile,
  );
}

function requirementTone(status: string): string {
  if (status === "ok" || status === "not_required") return "text-primary";
  if (status === "expired" || status === "missing" || status === "rejected") return "text-destructive";
  return "text-warning";
}

function normalizeCrewStatus(status: string | null | undefined): string {
  return compact(status).toLowerCase().replace(/\s+/g, "_");
}

function crewStatusTone(status: string | null | undefined): string {
  const normalized = normalizeCrewStatus(status);
  if (normalized === "active" || normalized === "approved") return "text-success";
  if (normalized === "invited" || normalized === "pending" || normalized === "under_review") return "text-warning";
  if (normalized === "blocked" || normalized === "inactive" || normalized === "rejected") return "text-destructive";
  return "text-muted-foreground";
}

function crewStatusLabel(status: string | null | undefined): string {
  const normalized = normalizeCrewStatus(status);
  if (!normalized) return "Unknown";
  return normalized
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function IdleReviewer() {
  const previewSteps = [
    { label: "Applicant", hint: "Identity & contact" },
    { label: "Compliance", hint: "License & insurance" },
    { label: "Requirements", hint: "Documents & assets" },
    { label: "Agreement", hint: "Legal sign-off" },
    { label: "Decision", hint: "Final approve / reject" },
  ];
  return (
    <Card className="sticky top-4 overflow-hidden">
      <div className="px-5 py-3 border-b bg-card/40 flex items-center gap-2">
        <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
        <span className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Reviewer</span>
        <span className="ml-auto text-[10px] text-muted-foreground/70">Idle</span>
      </div>
      <CardContent className="p-6 space-y-6">
        <div className="space-y-4">
          {previewSteps.map((s, i) => (
            <div key={s.label} className="flex gap-3 items-start">
              <div className="h-6 w-6 rounded-full border border-border bg-background flex items-center justify-center text-[10px] font-bold text-muted-foreground">
                {i + 1}
              </div>
              <div className="pt-0.5">
                <div className="text-sm font-medium text-muted-foreground">{s.label}</div>
                <div className="text-xs text-muted-foreground/70">{s.hint}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="rounded-lg border border-dashed border-border bg-muted/20 p-4 flex flex-col items-center text-center gap-2">
          <ClipboardList className="h-4 w-4 text-muted-foreground/60" />
          <p className="text-xs text-muted-foreground max-w-[220px]">Select an applicant on the left to begin the review.</p>
        </div>
      </CardContent>
    </Card>
  );
}

function getInviteDocuments(
  documents: ComplianceDocument[],
  token: OnboardingInviteToken,
  contractor: Contractor | null,
): ComplianceDocument[] {
  const ids = new Set(
    [token.contractorId, contractor?.id]
      .map((value) => String(value ?? "").trim())
      .filter(Boolean),
  );
  if (ids.size === 0) return [];
  return documents
    .filter((doc) => {
      if (doc.supersededBy) return false;
      if (ids.has(doc.contractorId)) return true;
      if (ids.has(metadataString(doc, "contractorId"))) return true;
      if (ids.has(metadataString(doc, "contractor_id"))) return true;

      const orgId = String(contractor?.organizationId ?? "").trim();
      const companyName = String(contractor?.companyName ?? "").trim().toLowerCase();
      const docOrgId = metadataString(doc, "organizationId");
      const docCompanyName = metadataString(doc, "contractorCompanyName").toLowerCase();
      return Boolean(orgId && companyName && docOrgId === orgId && docCompanyName === companyName);
    })
    .sort((a, b) => Date.parse(b.uploadedAt) - Date.parse(a.uploadedAt));
}

function InviteReviewer({
  token,
  contractor,
  documents,
  canDecide,
  onClose,
  onViewDocument,
  onVerifyDocument,
  onRejectDocument,
  rejectTarget,
  rejectReason,
  setRejectReason,
  clearRejectTarget,
  handleRejectConfirm,
  documentPreview,
  closeDocumentPreview,
}: {
  token: OnboardingInviteToken;
  contractor: Contractor | null;
  documents: ComplianceDocument[];
  canDecide: boolean;
  onClose: () => void;
  onViewDocument: (doc: ComplianceDocument) => void | Promise<void>;
  onVerifyDocument: (documentId: string) => void | Promise<void>;
  onRejectDocument: (doc: ComplianceDocument) => void;
  rejectTarget: { contractorId: string; documentId: string } | null;
  rejectReason: string;
  setRejectReason: (value: string) => void;
  clearRejectTarget: () => void;
  handleRejectConfirm: () => void | Promise<void>;
  documentPreview: { doc: ComplianceDocument; href: string } | null;
  closeDocumentPreview: () => void;
}) {
  const docs = getInviteDocuments(documents, token, contractor);
  const docGroups = groupComplianceDocumentsByOwner(docs, contractor);
  const summary = summarizeDocuments(docs);
  const created = new Date(token.createdAt).toLocaleString();
  const expires = new Date(token.expiresAt).toLocaleString();

  return (
    <Card className="sticky top-4" data-testid="unified-reviewer-panel">
      <CardContent className="p-0">
        <div className="flex items-start justify-between gap-3 p-4 border-b">
          <div className="min-w-0">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Invite details</div>
            <div className="text-base font-semibold truncate">
              {contractor?.companyName || `${token.entityType} invite ${token.id.slice(-6)}`}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {token.entityType} / {token.status}
            </div>
          </div>
          <Button size="icon" variant="ghost" onClick={onClose} aria-label="Close reviewer">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-4 space-y-5 max-h-[70vh] overflow-y-auto">
          <section className="space-y-2 text-sm">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Applicant / invite</div>
            <Row label="Company" value={contractor?.companyName} />
            <Row label="Contact" value={contractor?.contactName} />
            <Row label="Email" value={contractor?.email} />
            <Row label="Phone" value={contractor?.phone} />
            <Row label="Entity" value={token.entityType} />
            <Row label="Trade" value={contractor?.tradeType} />
            <Row label="Service area" value={contractor?.serviceArea} />
            <Row label="Created" value={created} />
            <Row label="Expires" value={expires} />
            <Row label="Created by" value={token.createdBy} />
          </section>

          <section className="space-y-3 border-t pt-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Compliance docs</div>
                <p className="text-xs text-muted-foreground">
                  Visible before onboarding starts when documents are linked to the contractor.
                </p>
              </div>
              <div className="flex items-center gap-1 text-[11px]">
                <Badge variant="outline" className="text-success">{summary.verified} verified</Badge>
                <Badge variant="outline" className="text-warning">{summary.pending} pending</Badge>
                {summary.rejected > 0 && <Badge variant="outline" className="text-destructive">{summary.rejected} rejected</Badge>}
              </div>
            </div>
            {docGroups.length === 0 ? (
              <p className="text-xs text-muted-foreground">No linked documents on file yet.</p>
            ) : (
              docGroups.map((group) => (
                <div key={group.key} className="space-y-1.5 rounded-md border p-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">{group.label}</p>
                    <p className="truncate text-[11px] text-muted-foreground">{group.subtitle}</p>
                  </div>
                  {group.docs.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between gap-2 border rounded px-2 py-1.5 text-xs">
                      <div className="min-w-0">
                        <div className="font-mono truncate">{doc.type}</div>
                        <div className="text-muted-foreground truncate">
                          {doc.verifiedAt ? `verified ${new Date(doc.verifiedAt).toLocaleDateString()}` : doc.rejectedAt ? `rejected: ${doc.rejectionReason}` : "pending verification"}
                          {doc.expiresAt ? ` / exp ${new Date(doc.expiresAt).toLocaleDateString()}` : ""}
                        </div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        {getComplianceDocumentStoragePath(doc) && (
                          <Button size="sm" variant="outline" onClick={() => void onViewDocument(doc)}>View</Button>
                        )}
                        {!doc.verifiedAt && !doc.rejectedAt && canDecide && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => void onVerifyDocument(doc.id)}>Verify</Button>
                            <Button size="sm" variant="outline" onClick={() => onRejectDocument(doc)}>Reject</Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))
            )}
          </section>

          <section className="rounded-md border border-dashed bg-muted/20 p-3">
            <p className="text-xs font-medium">Onboarding has not started yet.</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Once the invite is consumed, this same reviewer will show session steps,
              agreement, payout, requirements, and approval actions.
            </p>
          </section>
        </div>
      </CardContent>

      <RejectDocumentDialog
        open={!!rejectTarget}
        onOpenChange={(open) => !open && clearRejectTarget()}
        rejectReason={rejectReason}
        setRejectReason={setRejectReason}
        onConfirm={handleRejectConfirm}
      />

      <DocumentPreviewDialog
        preview={documentPreview}
        onClose={closeDocumentPreview}
      />
    </Card>
  );
}

function Row({ label, value, multiline = false }: { label: string; value?: string | null; multiline?: boolean }) {
  return (
    <div className={multiline ? "space-y-0.5" : "flex items-baseline justify-between gap-3"}>
      <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className={`text-xs ${multiline ? "block whitespace-pre-wrap" : "text-right truncate max-w-[60%]"}`}>
        {value && String(value).trim() ? value : <span className="text-muted-foreground">-</span>}
      </span>
    </div>
  );
}

function AgreementPreviewDialog({
  open,
  onClose,
  session,
  contractor,
  variant,
  signature,
}: {
  open: boolean;
  onClose: () => void;
  session: OnboardingSession;
  contractor: Contractor | null;
  variant: AgreementVariant | null;
  signature: AgreementSignature | null;
}) {
  const label = variant ? AGREEMENT_LABELS[variant] : "Agreement";
  const draft = session.draft;
  const companyName = compact(draft.name) || contractor?.companyName || "Installer applicant";
  const contactName = compact(draft.contactName) || contractor?.contactName || signature?.typedName || "Authorized signer";
  const contactEmail = compact(draft.contactEmail) || contractor?.email || "";
  const signerName = signature?.typedName ?? signature?.signedBy ?? contactName;
  const version = signature?.version || AGREEMENT_VERSION;
  const is1099Msa = variant === "1099_msa";
  const signedAtLabel = signature ? new Date(signature.signedAt).toLocaleString() : "";

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-5xl overflow-hidden">
        <DialogHeader>
          <DialogTitle>{signature ? "Signed " : ""}{is1099Msa ? "1099 Master Service Agreement" : label}</DialogTitle>
          <DialogDescription>
            Version {version}. This view is generated from the stored onboarding agreement signature record.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[72vh] overflow-y-auto rounded-md border bg-muted/20 p-3">
          <article className="mx-auto max-w-3xl rounded-sm border bg-background p-6 text-sm shadow-sm">
            <header className="space-y-4 border-b pb-5 text-center">
              <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-muted-foreground">
                Koredor Installer Network
              </div>
              <div>
                <h2 className="text-2xl font-bold tracking-tight">
                  {is1099Msa ? "1099 Master Service Agreement" : label}
                </h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  Agreement version {version} / Variant {variant ?? "unknown"}
                </p>
              </div>
              <Badge variant="outline" className={signature ? "text-success" : "text-warning"}>
                {signature ? "Electronically signed" : "Unsigned preview"}
              </Badge>
            </header>

            <section className="grid gap-3 border-b py-5 sm:grid-cols-2">
              <Row label="Installer entity" value={companyName} />
              <Row label="Authorized signer" value={contactName} />
              <Row label="Signer email" value={contactEmail} />
              <Row label="Entity type" value={session.entityType} />
              <Row label="Session id" value={session.id} />
              <Row label="Contractor id" value={session.contractorId ?? contractor?.id} />
            </section>

            <section className="space-y-4 border-b py-5 leading-relaxed">
              {is1099Msa ? (
                <>
                  <p className="text-muted-foreground">{MSA_INTRO}</p>
                  {MSA_SECTIONS.map((section) => (
                  <div key={section.title}>
                    <h3 className="font-semibold">{section.title}</h3>
                    <div className="mt-1 space-y-1.5">
                      {section.paragraphs.map((paragraph) => (
                        <p key={paragraph} className="text-muted-foreground">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </div>
                  ))}
                </>
              ) : (
                <div className="rounded-md border border-dashed bg-muted/20 p-3 text-sm text-muted-foreground">
                  This onboarding requires {label}. The signed record below is the stored evidence for this agreement variant.
                </div>
              )}
            </section>

            <section className="space-y-4 pt-5">
              <div className="rounded-md border bg-muted/20 p-4">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                      Electronic signature
                    </div>
                    <div className="mt-1 text-sm font-semibold">
                      {signature ? "Captured during onboarding" : "No signature captured"}
                    </div>
                  </div>
                  <Badge variant="outline" className={signature ? "text-success" : "text-warning"}>
                    {signature ? "Signed" : "Pending"}
                  </Badge>
                </div>
                {signature ? (
                  <div className="space-y-3">
                    <div className="rounded border bg-background px-4 py-3">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Typed legal name</div>
                      <div className="mt-1 font-serif text-2xl italic text-foreground">{signerName}</div>
                    </div>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <Row label="Signed by" value={signature.signedBy} />
                      <Row label="Signed at" value={signedAtLabel} />
                      <Row label="IP address" value={signature.ipAddress} />
                      <Row label="Signature id" value={signature.id} />
                      <Row label="Signed version" value={signature.version} />
                      <Row label="Agreement variant" value={signature.variant} />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Signature acknowledgement: I have read and agree to the terms of the {label}.
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    This agreement has not been signed yet for the selected onboarding.
                  </p>
                )}
              </div>
            </section>
          </article>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReadinessSummary({ readiness }: { readiness: ReturnType<ReturnType<typeof useOnboarding>["getAdminApprovalReadiness"]> | null }) {
  if (!readiness || (readiness.blockers.length === 0 && readiness.warnings.length === 0)) {
    return (
      <p className="text-xs text-muted-foreground inline-flex items-center gap-1">
        <CheckCircle2 className="h-3.5 w-3.5 text-success" />
        No blockers or warnings - ready for decision.
      </p>
    );
  }
  return (
    <div className="space-y-1">
      {readiness.blockers.map((b, i) => (
        <div key={`b-${i}`} className="flex items-start gap-2 text-xs text-destructive">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>{b}</span>
        </div>
      ))}
      {readiness.warnings.map((w, i) => (
        <div key={`w-${i}`} className="flex items-start gap-2 text-xs text-warning">
          <Clock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>{w}</span>
        </div>
      ))}
    </div>
  );
}

type StepDescriptor = ReturnType<typeof getOnboardingSteps>[number];

function DecisionDialogs(props: {
  approveOpen: boolean;
  setApproveOpen: (open: boolean) => void;
  rejectOpen: boolean;
  setRejectOpen: (open: boolean) => void;
  reuploadOpen: boolean;
  setReuploadOpen: (open: boolean) => void;
  reason: string;
  setReason: (value: string) => void;
  confirmText: string;
  setConfirmText: (value: string) => void;
  reuploadSteps: string[];
  setReuploadSteps: React.Dispatch<React.SetStateAction<string[]>>;
  stepDescriptors: StepDescriptor[];
  session: OnboardingSession;
  handleApprove: () => void;
  handleRejectSession: () => void;
  handleReupload: () => void;
}) {
  return (
    <>
      <Dialog open={props.approveOpen} onOpenChange={props.setApproveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve onboarding</DialogTitle>
            <DialogDescription>
              Type <span className="font-mono font-bold">CONFIRM</span> and provide a reason ({MIN_REASON}+ chars) to approve.
            </DialogDescription>
          </DialogHeader>
          <Textarea value={props.reason} onChange={(e) => props.setReason(e.target.value)} rows={3} placeholder="Why this contractor is ready..." />
          <div>
            <Label className="text-xs">Type CONFIRM</Label>
            <Input value={props.confirmText} onChange={(e) => props.setConfirmText(e.target.value)} placeholder="CONFIRM" className="font-mono" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => props.setApproveOpen(false)}>Cancel</Button>
            <Button onClick={props.handleApprove} disabled={props.confirmText.trim() !== APPROVE_CONFIRM || props.reason.trim().length < MIN_REASON}>
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={props.rejectOpen} onOpenChange={props.setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject onboarding</DialogTitle>
            <DialogDescription>Provide a clear reason ({MIN_REASON}+ chars).</DialogDescription>
          </DialogHeader>
          <Textarea value={props.reason} onChange={(e) => props.setReason(e.target.value)} rows={3} />
          <DialogFooter>
            <Button variant="outline" onClick={() => props.setRejectOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={props.handleRejectSession} disabled={props.reason.trim().length < MIN_REASON}>Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={props.reuploadOpen} onOpenChange={props.setReuploadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request reupload</DialogTitle>
            <DialogDescription>Pick the steps to reopen and explain why.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {props.stepDescriptors.map((s) => (
              <label key={s.key} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={props.reuploadSteps.includes(s.key)}
                  onChange={(e) =>
                    props.setReuploadSteps((prev) =>
                      e.target.checked ? [...prev, s.key] : prev.filter((k) => k !== s.key),
                    )
                  }
                />
                {getOnboardingStepLabel(s.key, props.session)}
              </label>
            ))}
          </div>
          <Textarea value={props.reason} onChange={(e) => props.setReason(e.target.value)} rows={3} placeholder="Reason..." />
          <DialogFooter>
            <Button variant="outline" onClick={() => props.setReuploadOpen(false)}>Cancel</Button>
            <Button onClick={props.handleReupload} disabled={props.reason.trim().length < MIN_REASON || props.reuploadSteps.length === 0}>
              Request reupload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function RejectDocumentDialog(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rejectReason: string;
  setRejectReason: (value: string) => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject document</DialogTitle>
          <DialogDescription>Document stays in the ledger as rejected. Provide a brief reason.</DialogDescription>
        </DialogHeader>
        <Textarea value={props.rejectReason} onChange={(e) => props.setRejectReason(e.target.value)} rows={3} />
        <DialogFooter>
          <Button variant="outline" onClick={() => props.onOpenChange(false)}>Cancel</Button>
          <Button variant="destructive" onClick={props.onConfirm} disabled={props.rejectReason.trim().length < 5}>Reject</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DocumentPreviewDialog({
  preview,
  onClose,
}: {
  preview: { doc: ComplianceDocument; href: string } | null;
  onClose: () => void;
}) {
  const kind = preview ? getComplianceDocumentPreviewKind(preview.doc, preview.href) : "other";
  const title = preview ? getComplianceDocumentFileName(preview.doc) : "Compliance document";
  return (
    <Dialog open={!!preview} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-6xl overflow-hidden">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Previewing the compliance file in this review workspace.</DialogDescription>
        </DialogHeader>
        {preview && kind === "image" && (
          <div className="max-h-[72vh] overflow-auto rounded-md border bg-background">
            <img src={preview.href} alt={title} className="mx-auto max-h-[72vh] w-full object-contain" />
          </div>
        )}
        {preview && kind === "pdf" && (
          <iframe src={preview.href} title={title} className="h-[72vh] w-full rounded-md border bg-background" />
        )}
        {preview && kind === "other" && (
          <div className="space-y-3">
            <div className="rounded-md border bg-muted/20 p-3 text-sm text-muted-foreground">
              This file type may not render in every browser.
            </div>
            <iframe src={preview.href} title={title} className="h-[64vh] w-full rounded-md border bg-background" />
            <Button variant="outline" asChild>
              <a href={preview.href} target="_blank" rel="noreferrer">
                <ExternalLink className="h-3.5 w-3.5 mr-1" />
                Open in new tab
              </a>
            </Button>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
